/**
 * TonkRoom — real-time Tonk gameplay board.
 *
 * Transport: WebSocket to `/api/tonk/tables/{id}/ws?token=<jwt>`.
 *   · Incoming: `table_state`, `hand_started`, `action`, `hand_ended`,
 *     `timeout`, `error`, `ping`.
 *   · Outgoing: `{action, ...}` payloads for draw / discard / spread / drop.
 *
 * Layout (mobile-first): seats stacked at top, table center (pot / discard
 * / deck), then MY hand + action bar pinned to the bottom.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft, Loader2, PlayCircle,
    DoorOpen, Layers, ChevronsUp, Clock, Trophy, Wifi, WifiOff, Smile, User as UserIcon,
    Volume2, VolumeX, Share2, Pin, MessageCircle, Send, Wallet,
} from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import TonkFelt from '../components/TonkFelt';
import AvatarPicker from '../components/AvatarPicker';
import TonkDealAnim from '../components/TonkDealAnim';
import { playable } from '../components/FreePlayNote';
import {
    playDeal, playFlick, playThud, playSpread, playHit, playWin, playLoss,
    haptic, isMuted, setMuted,
} from '../lib/tonkSounds';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const THEME = {
    accent: '#F5C542',
    felt: '#0E4A2A',
    pageBg: 'radial-gradient(ellipse at 50% -10%, #0E4A2A 0%, #052012 55%, #030B08 100%)',
    circleBg: 'radial-gradient(ellipse at 50% 30%, rgba(14,74,42,0.85) 0%, rgba(3,11,8,0.95) 70%)',
    headingFont: '"Bebas Neue", "Oswald", Impact, sans-serif',
    headingLetter: '0.03em',
};

const SUIT_CHAR = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_RED = { H: true, D: true };
const RANK_STR = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };

// Reactions: short code → emoji char. Kept intentionally small so the rail
// stays tappable one-handed and the vocabulary feels curated, not spam.
const REACTIONS = {
    fist:   { char: '\u{1F44A}', label: 'Fist bump' },
    fire:   { char: '\u{1F525}', label: 'Fire' },
    sweat:  { char: '\u{1F605}', label: 'Yikes' },
    skull:  { char: '\u{1F480}', label: 'Dead' },
    cards:  { char: '\u{1F0CF}', label: 'Joker' },
    target: { char: '\u{1F3AF}', label: 'Bullseye' },
};

const rankStr = (r) => RANK_STR[r] || String(r);
const cardKey = (c) => `${c[0]}-${c[1]}`;
const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Card value used for hand-total display (mirrors backend engine.card_value)
const cardValue = (c) => {
    const r = c[0];
    if (r === 1) return 1;
    if (r >= 10) return 10;
    return r;
};
const handTotal = (hand) => (hand || []).reduce((s, c) => s + cardValue(c), 0);

// ─── Card face ─────────────────────────────────────────────────────────
const CardFace = ({ card, small, selected, suggested, pinned, onClick, onQuickTap, onSwipeUp, onLongPress, disabled, dim }) => {
    const [rank, suit] = card;
    const red = SUIT_RED[suit];
    // Card sizes: `small` is opponents/spreads. Regular (user's hand
    // + winner reveal) uses viewport-fraction sizing so 6 cards ALWAYS
    // fit on iPhone widths without clipping. Formula: 6 cards ×
    // (viewport - 2rem side padding) / 6 minus gaps, capped at 3.5rem
    // (56px) on wider screens.
    // Reported 2026-07-22: "cards are still slightly too big — cuts off
    // a card when I have 6 in hand."
    const size = small ? 'w-11 h-16 text-xs' : 'w-[calc((100vw-2rem)/6.4)] max-w-14 aspect-[2/3] text-base';

    // Gesture handling:
    //   · Swipe UP (55+ px)  → discard.
    //   · Quick tap (< 350 ms, no drag) → `onQuickTap` (also discard,
    //     the "just click it" flow the user asked for).
    //   · Long press (≥ 400 ms, no drag) → `onLongPress` if provided,
    //     otherwise `onClick`. During reorder mode this is repurposed
    //     as "toggle pin", so a rank/suit sort can't shuffle your
    //     saved card away.
    const startYRef = useRef(null);
    const crossedRef = useRef(false);
    const longPressedRef = useRef(false);
    const longPressTimerRef = useRef(null);
    const [dragY, setDragY] = useState(0);
    const [gesturing, setGesturing] = useState(false);

    const clearLongPressTimer = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const onPointerDown = (e) => {
        if (disabled) return;
        startYRef.current = e.clientY;
        crossedRef.current = false;
        longPressedRef.current = false;
        setDragY(0);
        setGesturing(false);
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
        // Long-press: prefer `onLongPress` when provided (pin/unpin in
        // reorder mode), else fall back to `onClick` (select-for-spread
        // in draw mode).
        clearLongPressTimer();
        longPressTimerRef.current = setTimeout(() => {
            if (!gesturing && startYRef.current != null) {
                longPressedRef.current = true;
                haptic(20);
                if (onLongPress) onLongPress(card);
                else if (onClick) onClick();
            }
        }, 400);
    };
    const onPointerMove = (e) => {
        if (startYRef.current == null) return;
        const dy = e.clientY - startYRef.current;
        // Any real movement cancels the long-press.
        if (Math.abs(dy) > 6) {
            clearLongPressTimer();
            setGesturing(true);
        }
        if (dy < 0) {
            setDragY(Math.max(dy, -80));
            if (!crossedRef.current && dy <= -55) {
                crossedRef.current = true;
                haptic(15);
                playFlick(0.18);
            }
            if (crossedRef.current && dy > -55) crossedRef.current = false;
        }
    };
    const onPointerUp = (e) => {
        clearLongPressTimer();
        const startY = startYRef.current;
        startYRef.current = null;
        const dy = startY != null ? e.clientY - startY : 0;
        // Swipe up wins — dispatch discard.
        if (dy < -55 && onSwipeUp) {
            onSwipeUp(card);
        } else if (!gesturing && !longPressedRef.current) {
            // Any tap that wasn't a drag or a long-press → treat as a
            // "quick tap" (discard). Falls back to onClick if the parent
            // didn't provide onQuickTap.
            if (onQuickTap) onQuickTap(card);
            else if (onClick) onClick();
        }
        setDragY(0);
        setGesturing(false);
    };
    const onPointerCancel = () => {
        clearLongPressTimer();
        startYRef.current = null;
        setDragY(0);
        setGesturing(false);
    };

    const yOffset = dragY || (selected ? -8 : suggested ? -2 : 0);
    const shadow = dragY < -20
        ? '0 8px 20px rgba(0,0,0,0.6), 0 0 16px rgba(245,197,66,0.55)'
        : selected
            ? '0 0 18px rgba(245,197,66,0.75)'
            : suggested
                ? '0 0 10px rgba(245,197,66,0.35)'
                : undefined;

    return (
        <button
            type="button"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            disabled={disabled}
            className={`${size} relative rounded-lg border-2 flex flex-col items-center justify-between p-1 shrink-0 select-none touch-none ${dim ? 'opacity-40' : ''}`}
            style={{
                background: '#FDFCF4',
                borderColor: selected ? THEME.accent : suggested ? 'rgba(245,197,66,0.75)' : '#111',
                color: red ? '#C51F1F' : '#000',
                cursor: disabled ? 'default' : 'grab',
                transform: `translateY(${yOffset}px)`,
                boxShadow: shadow,
                transition: startYRef.current == null ? 'transform 0.15s ease' : 'none',
            }}
            data-testid={`tonk-card-${rank}-${suit}${selected ? '-selected' : suggested ? '-suggested' : ''}${pinned ? '-pinned' : ''}`}
        >
            {pinned && (
                <span
                    className="absolute -top-1.5 -right-1.5 rounded-full bg-[#F5C542] w-4 h-4 flex items-center justify-center border border-black"
                    style={{ boxShadow: '0 0 6px rgba(245,197,66,0.9)' }}
                    aria-label="pinned"
                >
                    <Pin className="w-2.5 h-2.5 text-black" strokeWidth={3} />
                </span>
            )}
            <span className="font-black leading-none self-start">{rankStr(rank)}</span>
            <span className="text-2xl leading-none">{SUIT_CHAR[suit]}</span>
            <span className="font-black leading-none self-end rotate-180">{rankStr(rank)}</span>
        </button>
    );
};

/** Display-only card face (no interaction). Used inside gesture wrappers
 *  where the parent needs to own the pointer events. */
const StaticCardFace = ({ card }) => {
    const [rank, suit] = card;
    const red = SUIT_RED[suit];
    return (
        <div
            className="w-14 h-20 rounded-lg border-2 border-black flex flex-col items-center justify-between p-1 shrink-0"
            style={{ background: '#FDFCF4', color: red ? '#C51F1F' : '#000' }}
        >
            <span className="font-black leading-none self-start">{rankStr(rank)}</span>
            <span className="text-2xl leading-none">{SUIT_CHAR[suit]}</span>
            <span className="font-black leading-none self-end rotate-180">{rankStr(rank)}</span>
        </div>
    );
};

const CardBack = ({ small }) => {
    const size = small ? 'w-10 h-14' : 'w-14 h-20';
    return (
        <div
            className={`${size} rounded-lg border-2 border-black shrink-0`}
            style={{
                background: 'repeating-linear-gradient(45deg, #C81E29 0 6px, #8A0F17 6px 12px)',
                boxShadow: 'inset 0 0 6px rgba(0,0,0,0.5)',
            }}
            aria-label="Face-down card"
        />
    );
};

/**
 * Discard-pile visual with swipe-down-to-draw. When enabled, dragging the
 * top card down by 55+ px pulls it into your hand (draw from discard).
 * A plain tap still works too.
 */
const SwipableDiscard = ({ card, enabled, onDraw }) => {
    const startYRef = useRef(null);
    const crossedRef = useRef(false);
    const [dy, setDy] = useState(0);
    const [gesturing, setGesturing] = useState(false);

    const onPointerDown = (e) => {
        if (!enabled) return;
        startYRef.current = e.clientY;
        crossedRef.current = false;
        setDy(0);
        setGesturing(false);
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    };
    const onPointerMove = (e) => {
        if (startYRef.current == null) return;
        const d = e.clientY - startYRef.current;
        if (d > 0) {
            setDy(Math.min(d, 80));
            if (d > 6) setGesturing(true);
            if (!crossedRef.current && d >= 55) {
                crossedRef.current = true;
                haptic(15);
                playFlick(0.18);
            }
            if (crossedRef.current && d < 55) crossedRef.current = false;
        }
    };
    const onPointerUp = (e) => {
        const startY = startYRef.current;
        startYRef.current = null;
        const d = startY != null ? e.clientY - startY : 0;
        if (enabled && (d > 55 || !gesturing)) {
            onDraw && onDraw();
        }
        setDy(0);
        setGesturing(false);
    };
    const onPointerCancel = () => {
        startYRef.current = null;
        setDy(0);
        setGesturing(false);
    };

    const hint = dy > 20 ? '\u2193 release to pull' : enabled ? 'swipe \u2193 to pull' : 'DISCARD';

    return (
        <div className="flex flex-col items-center gap-1" data-testid="tonk-draw-discard-btn">
            <div
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                className="select-none touch-none"
                style={{
                    transform: `translateY(${dy}px)`,
                    transition: startYRef.current == null ? 'transform 0.15s ease' : 'none',
                    opacity: enabled ? 1 : 0.5,
                    cursor: enabled ? 'grab' : 'default',
                }}
            >
                {card ? (
                    <StaticCardFace card={card} />
                ) : (
                    <div className="w-14 h-20 rounded-lg border-2 border-dashed border-white/20" />
                )}
            </div>
            <span className={`text-[10px] ${enabled && dy > 20 ? 'text-[#F5C542] font-black' : 'text-white/70'}`}>
                {hint}
            </span>
        </div>
    );
};

// ─── Seat pill (opponents) ─────────────────────────────────────────────
// ─── Page-level error boundary ─────────────────────────────────────────
// Reported 2026-07-24 (post-v152): "I tonked out and both of our screens
// went black." The previous fix (v145) only boundaried the HandEndOverlay
// — if ANY other child render throws during a hand-end frame (bad seat
// index, missing chat bubble ref, weird animation math), the whole
// TonkRoom tree unmounted and iOS Safari painted a black void. A page
// boundary swallows the error, shows a Refresh button, and never leaves
// the player staring at black.
class TonkPageBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false, message: '' };
    }
    static getDerivedStateFromError(err) {
        return { failed: true, message: (err && err.message) || 'Unknown error' };
    }
    componentDidCatch(err, info) {
        try {
            console.error('TonkPageBoundary crashed:', err, info);
        } catch { /* ignore */ }
        // Fire-and-forget crash report so the admin panel can review
        // patterns without needing the player to describe repro steps.
        try {
            const API = process.env.REACT_APP_BACKEND_URL;
            let swCache = '';
            try {
                const nav = navigator?.serviceWorker;
                if (nav?.controller) swCache = 'sw-controller-active';
            } catch { /* noop */ }
            // Use fetch (not axios) so we don't need the axios auth interceptor
            // and can post credentials for auto-user-attribution.
            fetch(`${API}/api/errors/tonk-crash`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: (err && err.message) || String(err || 'unknown'),
                    stack: err && err.stack ? String(err.stack).slice(0, 6000) : null,
                    component_stack: (info && info.componentStack)
                        ? String(info.componentStack).slice(0, 6000)
                        : null,
                    table_id: (window.location.pathname.match(/\/tonk\/([^/?#]+)/) || [])[1] || null,
                    url: window.location.href,
                    sw_cache: swCache || null,
                    context: {
                        viewport: `${window.innerWidth}x${window.innerHeight}`,
                        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
                    },
                }),
            }).catch(() => { /* silent — never let the reporter break the fallback UI */ });
        } catch { /* ignore */ }
    }
    render() {
        if (this.state.failed) {
            return (
                <div
                    className="a11y-font min-h-screen text-white flex flex-col items-center justify-center p-6 text-center gap-4"
                    style={{ background: THEME.pageBg }}
                    data-testid="tonk-page-crash-fallback"
                >
                    <div className="text-5xl">🃏</div>
                    <h1 className="text-2xl font-black" style={{ fontFamily: THEME.headingFont }}>
                        Table hiccup
                    </h1>
                    <p className="text-white/80 max-w-md text-sm leading-relaxed">
                        Something in the felt render tripped up — but your seat, chips,
                        and the hand result are all safe on the server. Tap below to
                        rejoin without losing anything.
                    </p>
                    <div className="flex gap-2 flex-wrap justify-center">
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="px-6 py-3 rounded-xl font-black bg-[#F5C542] text-[#0a1f14] hover:brightness-110"
                            data-testid="tonk-page-crash-reload"
                        >
                            Rejoin table
                        </button>
                        <button
                            type="button"
                            onClick={() => { window.location.href = '/tonk'; }}
                            className="px-6 py-3 rounded-xl font-black bg-white/10 border border-white/20 hover:bg-white/20"
                            data-testid="tonk-page-crash-lobby"
                        >
                            Back to lobby
                        </button>
                    </div>
                    {this.state.message && (
                        <p className="mt-4 text-[10px] font-mono text-white/40 max-w-md break-all">
                            {this.state.message}
                        </p>
                    )}
                </div>
            );
        }
        return this.props.children;
    }
}

// ─── Main component ────────────────────────────────────────────────────
export default function TonkRoom() {
    return (
        <TonkPageBoundary>
            <TonkRoomInner />
        </TonkPageBoundary>
    );
}

function TonkRoomInner() {
    const { id: tableId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const joinCode = searchParams.get('code');

    const [table, setTable] = useState(null);
    const [wsLive, setWsLive] = useState(false);
    // Connection-quality: latency (ms) from the last successful ping,
    // and the wall-clock ts of the last pong. Drives the small dot
    // rendered next to the deck. Green = < 400ms, amber = 400–1200ms,
    // red = > 1200ms or stale.
    const [pingMs, setPingMs] = useState(null);
    const [lastPongMs, setLastPongMs] = useState(null);
    // Mirror the last-pong wall clock into a ref so background timers
    // (zombie-WS watchdog, visibilitychange handler) can read the freshest
    // value without re-subscribing every render. State + ref stay in sync.
    const lastPongMsRef = useRef(null);
    // Highest hand_number we've either seen live via WS or acknowledged
    // via a REST catch-up. Used to synthesize a hand-end banner when a
    // hand_ended message is dropped (iOS Safari tab-suspend etc.).
    const lastSeenHandRef = useRef(0);
    const [selected, setSelected] = useState([]); // selected cards from my hand
    const [starting, setStarting] = useState(false);
    const [handEndBanner, setHandEndBanner] = useState(null);
    // Duplicate-banner guard — dedupes the "CAUGHT" / "TONK OUT" overlay so
    // a spamming backend (or repeated snapshots on reconnect) can't loop
    // the same winner every second. Keyed on `hand_number|winner_seat|payout`
    // + a rolling timestamp; won't re-fire the SAME key inside 12 seconds.
    // Root cause of the "Tonk just keeps showing winners" report, Feb 2026.
    const lastBannerKeyRef = useRef({ key: null, at: 0 });
    const shouldShowBanner = (data) => {
        const key = `${data?.hand_number ?? '?'}|${data?.winner_seat ?? '?'}|${data?.payout ?? '?'}|${data?.win_type ?? '?'}`;
        const now = Date.now();
        const prev = lastBannerKeyRef.current || { key: null, at: 0 };
        if (prev.key === key && (now - prev.at) < 12_000) return false;
        lastBannerKeyRef.current = { key, at: now };
        return true;
    };
    // Track which hand-end we've already banner'd so the self-heal effect
    // below doesn't re-trigger the banner every render.
    const bannerAckedHandRef = useRef(0);
    // Recent winners strip — populated from /recent-hands + appended on each hand_ended WS event
    const [recentHands, setRecentHands] = useState([]);
    // Celebration burst — key changes on each hand_ended so the confetti re-mounts and replays
    const [celebrationKey, setCelebrationKey] = useState(0);
    // Floating reactions — recent bubbles shown above the center table
    const [reactions, setReactions] = useState([]);
    const [reactionOpen, setReactionOpen] = useState(false);
    // Chat — recent bubbles shown over each seat's avatar for ~5s
    const [chatMsgs, setChatMsgs] = useState([]);
    const [chatOpen, setChatOpen] = useState(false);
    const [chatDraft, setChatDraft] = useState('');
    const lastChatAtRef = useRef(0);
    // Muted player keys — user_id for humans OR display_name for bots.
    // Persisted in localStorage so a mute survives page reloads. Bubbles
    // + reactions from these players are suppressed for you only.
    const [mutedKeys, setMutedKeys] = useState(() => {
        try {
            const raw = localStorage.getItem('tonk_muted_v1');
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });
    const toggleMute = useCallback((key) => {
        if (!key) return;
        setMutedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
                toast.success('Unmuted');
            } else {
                next.add(key);
                toast('Muted \u2014 tap their bubble again to unmute', { icon: '\ud83d\udd07' });
            }
            try { localStorage.setItem('tonk_muted_v1', JSON.stringify([...next])); } catch { /* ignore */ }
            return next;
        });
    }, []);
    // Helper to derive a mute-key from a seat.
    const muteKeyForSeat = useCallback((seat) => {
        if (!seat) return null;
        return seat.user_id ? `u:${seat.user_id}` : `b:${seat.display_name || `seat-${seat.seat_index}`}`;
    }, []);
    const [avatarOpen, setAvatarOpen] = useState(false);
    const [myAvatar, setMyAvatar] = useState({ kind: null, value: null });
    const [muted, setMutedState] = useState(isMuted());
    // Personal hand-arrangement — client-side only. Users often want to
    // group by rank/suit to spot spreads. Stored as an ordered list of
    // card keys (e.g. "10-H"). Reconciled with the server-side hand
    // whenever cards arrive/leave.
    const [handOrder, setHandOrder] = useState([]);
    // Swap picker — index of the card currently "picked up" for tap-swap
    // (only enabled when it's NOT my turn OR I've already discarded).
    const [swapIdx, setSwapIdx] = useState(null);
    // Pinned card keys (Set) — long-press during reorder mode toggles pin.
    // Pinned cards keep their slot when Rank/Suit sort runs. Purely
    // client-side, never synced to the server.
    const [pinnedKeys, setPinnedKeys] = useState(() => new Set());
    // Optimistic discard: track cards we've already fired an action for
    // but haven't yet seen removed by the server's WS state broadcast.
    // Hiding them locally on tap makes the discard feel instantaneous;
    // the server broadcast (~150-300 ms later) is the source of truth.
    const [pendingRemoveKeys, setPendingRemoveKeys] = useState(() => new Set());
    // Guard against rapid double-taps sending 2 identical actions.
    const inFlightActionRef = useRef(null);
    // Mirror the ref as state so the turn-timer effect can pause the
    // countdown while an action is in flight. Otherwise on slow
    // connections mom watches the timer tick to zero AFTER she already
    // tapped — feels like the tap did nothing even though it's just
    // waiting on the server.
    const [actionInFlight, setActionInFlight] = useState(false);
    // Flying-cards animation: triggered when hand_number increases + we're in_hand
    const [dealAnimId, setDealAnimId] = useState(0);
    const [dealActive, setDealActive] = useState(false);
    const lastAnimHandRef = useRef(0);
    const lastReactionAtRef = useRef(0);
    const wsRef = useRef(null);

    // Turn-timer visual — updated by a local ticker driven by turn_started_at.
    //
    // Also: `parseUTC` normalizes the ISO string. Mongo's Motor driver
    // returns naive datetimes (UTC value but no tzinfo), which Pydantic
    // then serializes as e.g. "2026-07-21T14:30:00.123" (no `Z`). Safari
    // parses those as LOCAL time, producing a negative elapsed and
    // pinning the timer at its starting value. Appending `Z` when the
    // string has no timezone forces UTC parsing.
    const parseUTC = (iso) => {
        if (!iso) return NaN;
        const s = String(iso);
        const hasTZ = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
        return new Date(hasTZ ? s : s + 'Z').getTime();
    };
    const [turnFrac, setTurnFrac] = useState(1);
    const timerHealedRef = useRef(null);
    // Ref-forward so the timer effect (declared early) can call
    // refreshTable (declared later) without a TDZ crash. Wired up
    // immediately below refreshTable's own definition.
    const refreshTableRef = useRef(null);
    useEffect(() => {
        if (!table || table.phase !== 'in_hand' || !table.turn_started_at) {
            setTurnFrac(1);
            return;
        }
        const started = parseUTC(table.turn_started_at);
        const startedKey = String(started);
        const secs = table.rules?.turn_timer_seconds || 20;
        const tick = () => {
            // Freeze the countdown mid-tap so mom doesn't see it hit
            // zero while she's waiting on a slow round-trip. As soon as
            // the response arrives (success or error), the tick resumes
            // from the current server-authoritative timestamp.
            if (actionInFlight) return;
            const elapsed = (Date.now() - started) / 1000;
            const frac = Math.max(0, Math.min(1, 1 - elapsed / secs));
            setTurnFrac(frac);
            // Self-heal: if the timer has been past zero for > 3 seconds
            // and the table snapshot still says the same turn, our WS
            // probably dropped and we missed the server-side auto-play
            // broadcast. Fetch fresh state so we're not stuck staring at
            // a frozen "0s" display. Fire once per turn.
            if (elapsed > secs + 3 && timerHealedRef.current !== startedKey) {
                timerHealedRef.current = startedKey;
                refreshTableRef.current?.();
            }
        };
        tick();
        const id = setInterval(tick, 200);
        return () => clearInterval(id);
    }, [table?.turn_started_at, table?.phase, table?.rules?.turn_timer_seconds, actionInFlight]);

    // Next-hand countdown — starts when a hand ends and auto-deal is
    // scheduled server-side. Shows "Next hand in 8s…" so the player
    // knows they don't need to click anything.
    const [nextHandSecs, setNextHandSecs] = useState(null);
    useEffect(() => {
        if (!table || table.phase !== 'hand_ended' || !table.hand_ended_at) {
            setNextHandSecs(null);
            return undefined;
        }
        const endedAt = parseUTC(table.hand_ended_at);
        const total = table.rules?.next_hand_auto_deal_seconds ?? 10;
        if (!total || total <= 0) {
            setNextHandSecs(null);
            return undefined;
        }
        const tick = () => {
            const remaining = Math.max(0, total - Math.floor((Date.now() - endedAt) / 1000));
            setNextHandSecs(remaining);
        };
        tick();
        const id = setInterval(tick, 500);
        return () => clearInterval(id);
    }, [table?.hand_ended_at, table?.phase, table?.rules?.next_hand_auto_deal_seconds]);

    // Fetch my avatar so the "edit avatar" button preview matches
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/user/avatar`, { withCredentials: true });
                setMyAvatar({ kind: data?.kind || null, value: data?.value || null });
            } catch { /* ignore */ }
        })();
    }, []);

    // Fetch initial recent-hands so late joiners see who's been winning
    useEffect(() => {
        if (!tableId) return;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/tonk/tables/${tableId}/recent-hands?limit=5`,
                    { withCredentials: true },
                );
                setRecentHands(data?.hands || []);
            } catch { /* strip is optional — silent */ }
        })();
    }, [tableId]);

    // Fetch initial table + auto-join if not seated
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/tonk/tables/${tableId}`, { withCredentials: true });
                if (cancelled) return;
                setTable(data);
                const meSeated = data.seats?.some((s) => s.user_id === user?.id);
                if (!meSeated && data.phase === 'waiting') {
                    // Try to join automatically
                    try {
                        const body = joinCode ? { join_code: joinCode } : {};
                        const { data: joined } = await axios.post(
                            `${API}/tonk/tables/${tableId}/join`, body, { withCredentials: true },
                        );
                        if (!cancelled) setTable(joined);
                    } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Could not join table');
                    }
                }
            } catch (err) {
                toast.error(err?.response?.data?.detail || 'Table not found');
                nav('/casino/tonk');
            }
        })();
        return () => { cancelled = true; };
    }, [tableId, user?.id, joinCode, nav]);

    // WebSocket connect w/ exponential backoff
    useEffect(() => {
        if (!table) return;
        let mounted = true;
        let backoff = 800;
        let reconnectTimer = null;

        const connect = () => {
            if (!mounted) return;
            const token = localStorage.getItem('access_token');
            if (!token) return;
            const wsBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/, 'ws');
            const url = `${wsBase}/api/tonk/tables/${tableId}/ws?token=${encodeURIComponent(token)}`;
            let ws;
            try { ws = new WebSocket(url); } catch { schedule(); return; }
            wsRef.current = ws;
            ws.onopen = () => {
                if (!mounted) return;
                setWsLive(true);
                backoff = 800;
                const now = Date.now();
                setLastPongMs(now);
                lastPongMsRef.current = now;
                // Immediately catch up on state we might have missed while
                // the socket was down. iOS Safari can silently kill a WS
                // for minutes when the tab backgrounds; without this pull,
                // a hand may have ended, been paid out, and the next one
                // started — the client would just show blank until the
                // next server broadcast.
                refreshTableRef.current?.();
                // Fetch recent-hands so we can synthesize a hand-end
                // banner for any hand we missed during the outage.
                try {
                    axios.get(`${API}/tonk/tables/${tableId}/recent-hands?limit=1`, { withCredentials: true })
                        .then(({ data }) => {
                            const latest = (data?.hands || [])[0];
                            if (!latest) return;
                            if (latest.hand_number && latest.hand_number > lastSeenHandRef.current) {
                                lastSeenHandRef.current = latest.hand_number;
                                bannerAckedHandRef.current = latest.hand_number;
                                const catchupData = {
                                    kind: latest.kind,
                                    hand_number: latest.hand_number,
                                    winner_seat: latest.winner_seat,
                                    multiplier: latest.multiplier || 1,
                                    payout: latest.payout,
                                    catchup: true,
                                };
                                if (shouldShowBanner(catchupData)) {
                                    setHandEndBanner(catchupData);
                                }
                            }
                        })
                        .catch(() => { /* ignore — best effort */ });
                } catch { /* ignore */ }
            };
            ws.onmessage = (ev) => {
                if (!mounted) return;
                let msg;
                try { msg = JSON.parse(ev.data); } catch { return; }
                if (msg.type === 'ping') {
                    // Answer server keep-alives so it doesn't close us.
                    try { ws.send(JSON.stringify({ type: 'pong', ts: msg.ts })); } catch { /* noop */ }
                    return;
                }
                if (msg.type === 'pong') {
                    const now = Date.now();
                    setLastPongMs(now);
                    lastPongMsRef.current = now;
                    if (typeof msg.ts === 'number') {
                        setPingMs(Math.max(0, Date.now() - msg.ts));
                    }
                    return;
                }
                if (msg.table) setTable(msg.table);
                if (msg.type === 'hand_started') {
                    // Count active seats so the deal cadence matches the table
                    const n = (msg.table?.seats || []).filter((s) => s.user_id || s.is_bot).length || 2;
                    playDeal(5, n);
                }
                if (msg.type === 'action') {
                    setSelected([]);
                    const act = (msg.action || '').toLowerCase();
                    if (act === 'discard') playThud();
                    else if (act === 'draw') playFlick(0.22);
                    else if (act === 'spread') playSpread();
                    else if (act === 'hit') playHit();
                }
                if (msg.type === 'hand_ended') {
                    const summary = msg.summary || {};
                    const hn = msg.table?.hand_number || bannerAckedHandRef.current;
                    bannerAckedHandRef.current = hn;
                    lastSeenHandRef.current = Math.max(lastSeenHandRef.current, hn || 0);
                    const bannerData = {
                        ...summary,
                        hand_number: hn,
                        payout: msg.payout,
                    };
                    if (shouldShowBanner(bannerData)) {
                        setHandEndBanner(bannerData);
                        setCelebrationKey((k) => k + 1);
                    }
                    setRecentHands((prev) => [
                        {
                            hand_number: msg.table?.hand_number ?? null,
                            kind: summary.kind,
                            winner_seat: summary.winner_seat,
                            winner_is_bot: !!msg.table?.seats?.[summary.winner_seat]?.is_bot,
                            winner_user_id: msg.table?.seats?.[summary.winner_seat]?.user_id ?? null,
                            multiplier: summary.multiplier || 1,
                            payout: msg.payout,
                            ended_at: new Date().toISOString(),
                        },
                        ...prev,
                    ].slice(0, 5));
                    setSelected([]);
                    // Sound: win chime if MY seat won, loss buzzer otherwise
                    const iWon = mySeat != null && summary.winner_seat === mySeat.seat_index;
                    setTimeout(() => (iWon ? playWin() : playLoss()), 120);
                }
                if (msg.type === 'reaction' && REACTIONS[msg.emoji]) {
                    const bubble = {
                        id: `${msg.at || Date.now()}-${msg.seat ?? 'x'}-${Math.random().toString(36).slice(2, 6)}`,
                        emoji: msg.emoji,
                        name: msg.name || 'Player',
                        seat: msg.seat,
                        line: typeof msg.line === 'string' ? msg.line.slice(0, 60) : null,
                    };
                    setReactions((cur) => [...cur.slice(-6), bubble]);
                    // Lines linger a bit longer so you have time to read them.
                    const ttl = bubble.line ? 5500 : 4500;
                    setTimeout(() => {
                        setReactions((cur) => cur.filter((r) => r.id !== bubble.id));
                    }, ttl);
                }
                if (msg.type === 'chat' && typeof msg.text === 'string' && msg.seat != null) {
                    const bubble = {
                        id: `${msg.at || Date.now()}-${msg.seat}-${Math.random().toString(36).slice(2, 6)}`,
                        seat: msg.seat,
                        name: msg.name || 'Player',
                        text: msg.text.slice(0, 120),
                    };
                    // Keep at most 8 in memory; auto-remove after 5.5s.
                    setChatMsgs((cur) => [...cur.slice(-7), bubble]);
                    setTimeout(() => {
                        setChatMsgs((cur) => cur.filter((m) => m.id !== bubble.id));
                    }, 5500);
                }
                if (msg.type === 'error' && msg.detail) toast.error(msg.detail);
                // Auto-play notice — someone (usually the local player)
                // ran out of turn time and the server auto-drew and/or
                // auto-discarded on their behalf. Show a friendly toast
                // so the user doesn't experience a silent turn-jump.
                if (msg.type === 'auto_played') {
                    const cardStr = Array.isArray(msg.card) && msg.card.length >= 2
                        ? `${msg.card[0]}${msg.card[1] === 'hearts' ? '♥' : msg.card[1] === 'diamonds' ? '♦' : msg.card[1] === 'clubs' ? '♣' : msg.card[1] === 'spades' ? '♠' : ''}`
                        : '';
                    const isMe = mySeat != null && msg.seat === mySeat.seat_index;
                    if (isMe) {
                        toast.warning(
                            `Timed out — auto-discarded ${cardStr} for you`,
                            { description: 'Tap faster next turn — your seat stayed in the hand.' },
                        );
                    } else {
                        toast.message(
                            `${msg.display_name || 'Player'} timed out (discarded ${cardStr})`,
                            { duration: 2000 },
                        );
                    }
                }
            };
            ws.onerror = () => { /* onclose will reconnect */ };
            ws.onclose = () => {
                if (!mounted) return;
                setWsLive(false);
                schedule();
            };
        };
        const schedule = () => {
            reconnectTimer = setTimeout(connect, backoff);
            backoff = Math.min(backoff * 1.7, 8000);
        };
        connect();
        return () => {
            mounted = false;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (wsRef.current) {
                try { wsRef.current.close(); } catch { /* ignore */ }
            }
        };
    }, [table?.id, tableId]);

    // Ping the WS every 5s to measure connection health. Frontend
    // sends `{type:"ping", ts:Date.now()}`, backend echoes back the
    // ts in a `{type:"pong"}` message, and the onmessage handler
    // computes round-trip latency + last-seen timestamp for the dot.
    useEffect(() => {
        const id = setInterval(() => {
            const ws = wsRef.current;
            if (!ws || ws.readyState !== 1 /* OPEN */) return;
            try { ws.send(JSON.stringify({ type: 'ping', ts: Date.now() })); } catch { /* noop */ }
        }, 5000);
        return () => clearInterval(id);
    }, []);

    // ── Zombie-WS watchdog ────────────────────────────────────────────
    // iOS Safari (esp. when the tab backgrounds, phone locks, or WiFi
    // hops) will silently stop delivering WS messages WITHOUT closing
    // the connection. `ws.readyState` stays OPEN forever, `onclose`
    // never fires — the client thinks it's live but is receiving no
    // hand_started / hand_ended / action messages. The 90% "have to
    // refresh" symptom reported 2026-07-23 was this exact bug.
    //
    // Fix: every 3s check the last-pong timestamp. If we haven't heard
    // ANYTHING back from the server in >12s (well past our 5s ping
    // interval), force-close the socket. `onclose` triggers reconnect,
    // and reconnect triggers refreshTable() so we catch up on state.
    useEffect(() => {
        const id = setInterval(() => {
            const ws = wsRef.current;
            if (!ws) return;
            if (ws.readyState !== 1 /* OPEN */) return;
            const last = lastPongMsRef.current;
            if (!last) return;
            if (Date.now() - last > 12000) {
                try { ws.close(4000, 'zombie'); } catch { /* ignore */ }
                // onclose handler flips wsLive=false and schedules reconnect.
            }
        }, 3000);
        return () => clearInterval(id);
    }, []);

    // ── Foreground-return handler ─────────────────────────────────────
    // When the tab comes back to the foreground on iOS Safari, force
    // an immediate REST refresh + WS health check. Without this, users
    // return to a stale table (hand ended without banner, next hand
    // already dealt) until the WS times out or they manually refresh.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            // Force a REST snapshot right now — even if the WS reports
            // live, we can't trust it after a sleep. Cheap and correct.
            refreshTableRef.current?.();
            const ws = wsRef.current;
            const last = lastPongMsRef.current;
            const staleFor = last ? Date.now() - last : Infinity;
            if (ws && ws.readyState === 1 && staleFor > 3000) {
                // Kick the socket — reconnect flow will refresh state again.
                try { ws.close(4001, 'foreground-check'); } catch { /* ignore */ }
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        window.addEventListener('pageshow', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
            window.removeEventListener('pageshow', onVisible);
        };
    }, []);

    // Auto-clear hand-end banner after 6s (or immediately when next hand starts)
    useEffect(() => {
        if (!handEndBanner) return;
        const id = setTimeout(() => setHandEndBanner(null), 6500);
        return () => clearTimeout(id);
    }, [handEndBanner]);
    useEffect(() => {
        if (table?.phase === 'in_hand') setHandEndBanner(null);
    }, [table?.phase]);

    // ─── SELF-HEAL: banner reconstruction on missed WS ──────────────
    // If the `hand_ended` WS message is dropped (iOS Safari backgrounded,
    // brief disconnect, aggressive service worker cache) but the state
    // snapshot still delivers `phase: hand_ended` + `last_hand_result`,
    // the winner banner would never appear and the user would see "hand
    // just skipped, no winner announced". Reported on 2026-07-22 —
    // "games ending, no winner announced, just fresh cards".
    //
    // Fix: whenever we notice the table is HAND_ENDED with a fresh
    // `hand_ended_at` (< 15s old) and we haven't already banner'd this
    // hand, reconstruct the banner from `last_hand_result`.
    useEffect(() => {
        if (!table) return;
        if (table.phase !== 'hand_ended') return;
        if (!table.last_hand_result) return;
        const handNo = table.hand_number || 0;
        if (bannerAckedHandRef.current === handNo) return;
        // Freshness gate — don't banner an ancient result on a page refresh.
        // `parseUTC` returns a millisecond timestamp (Number), NOT a Date.
        // Calling `.getTime()` on it was the crash that took both players'
        // screens black on tonk-out (repro 2026-07-24). Compare timestamps
        // directly.
        const endedAtMs = table.hand_ended_at ? parseUTC(table.hand_ended_at) : NaN;
        if (Number.isFinite(endedAtMs)) {
            const ageSec = (Date.now() - endedAtMs) / 1000;
            if (ageSec > 15) return;
        }
        bannerAckedHandRef.current = handNo;
        const reconstructed = {
            ...table.last_hand_result,
            hand_number: handNo,
            payout: table.last_hand_result.payout,
        };
        if (shouldShowBanner(reconstructed)) {
            setHandEndBanner(reconstructed);
        }
    }, [table?.phase, table?.hand_number, table?.hand_ended_at, table?.last_hand_result]);

    // Trigger the dealing animation once per new hand
    useEffect(() => {
        if (!table) return;
        if (table.phase !== 'in_hand') return;
        if (!table.hand_number || table.hand_number === lastAnimHandRef.current) return;
        lastAnimHandRef.current = table.hand_number;
        setDealAnimId((n) => n + 1);
        setDealActive(true);
    }, [table?.hand_number, table?.phase]);

    // Deal-recipient order: start from turn_index (the winner / first-to-act)
    // and go clockwise to preserve the "winner gets cards first" rule.
    const dealRecipientOrder = useMemo(() => {
        if (!table) return [];
        const active = table.seats.filter((s) => (s.user_id || s.is_bot));
        if (active.length === 0) return [];
        const startAt = table.seats.findIndex((s) => s.seat_index === table.turn_index);
        const ordered = [];
        for (let i = 0; i < table.seats.length; i += 1) {
            const idx = (startAt + i) % table.seats.length;
            const s = table.seats[idx];
            if (s && (s.user_id || s.is_bot)) ordered.push(s.seat_index);
        }
        return ordered;
    }, [table?.seats, table?.turn_index]);

    // ─── Derived seat info ──────────────────────────────────────────────
    const mySeat = useMemo(
        () => table?.seats?.find((s) => s.user_id === user?.id),
        [table, user?.id],
    );
    const isMyTurn = mySeat && table?.phase === 'in_hand' && table?.turn_index === mySeat.seat_index;
    const hasDrawn = !!table?.turn_has_drawn;

    // ─── Personal hand arrangement ──────────────────────────────────────
    // Keep `handOrder` in sync with the server-side hand: keep existing
    // ordering, append any newly-drawn cards to the end, drop any cards
    // that are gone (spread laid, discarded, etc.).
    useEffect(() => {
        const serverHand = mySeat?.hand || [];
        const serverKeys = serverHand.map(cardKey);
        setHandOrder((prev) => {
            const kept = prev.filter((k) => serverKeys.includes(k));
            const added = serverKeys.filter((k) => !kept.includes(k));
            const next = [...kept, ...added];
            // Bail if nothing changed to avoid a re-render loop.
            if (next.length === prev.length && next.every((v, i) => v === prev[i])) {
                return prev;
            }
            return next;
        });
    }, [mySeat?.hand]);

    // Cancel any pending tap-swap only when the ACTUAL card SET changes
    // (a card was drawn/laid/discarded). Deriving from the sorted-key
    // string sidesteps `mySeat.hand` array-identity churn on every WS
    // tick — otherwise the second tap of a swap loses the picked state
    // as soon as any WS message arrives (bot reactions, etc.).
    const handKeySig = useMemo(
        () => (mySeat?.hand || []).map(cardKey).sort().join('|'),
        [mySeat?.hand],
    );
    useEffect(() => {
        setSwapIdx(null);
        // Prune any pinned keys that are no longer in the hand — a
        // discarded/spread card obviously can't stay pinned.
        setPinnedKeys((prev) => {
            const live = new Set((mySeat?.hand || []).map(cardKey));
            const next = new Set([...prev].filter((k) => live.has(k)));
            return next.size === prev.size ? prev : next;
        });
    }, [handKeySig, mySeat?.hand]);

    // The visible hand — ordered by the user's personal arrangement,
    // AND optimistically stripped of any card we've just discarded so
    // the tap feels instantaneous. The WS state broadcast (100-300ms
    // later) reconciles the ref.
    const orderedHand = useMemo(() => {
        const byKey = new Map((mySeat?.hand || []).map((c) => [cardKey(c), c]));
        const out = [];
        for (const k of handOrder) {
            if (pendingRemoveKeys.has(k)) continue;
            const c = byKey.get(k);
            if (c) out.push(c);
        }
        // Any card not yet in handOrder (shouldn't happen after the effect
        // above, but be defensive) → append.
        for (const c of (mySeat?.hand || [])) {
            const k = cardKey(c);
            if (pendingRemoveKeys.has(k)) continue;
            if (!handOrder.includes(k)) out.push(c);
        }
        return out;
    }, [mySeat?.hand, handOrder, pendingRemoveKeys]);

    // Reordering is allowed when a discard isn't the next required action
    // (i.e. anytime EXCEPT "your turn AND you've drawn"). Tapping cards
    // during discard-required moments must still select-for-spread.
    const canReorder = !!mySeat && !(isMyTurn && hasDrawn);

    const sortHandBy = useCallback((mode) => {
        const hand = mySeat?.hand || [];
        if (!hand.length) return;
        const rankValue = (c) => (c[0] === 1 ? 14 : c[0]); // aces high when sorting
        const suitOrder = { S: 0, H: 1, D: 2, C: 3 };
        // Split into unpinned (sortable) and pinned (stay put).
        setHandOrder((prev) => {
            const positions = prev.length ? prev : hand.map(cardKey);
            const byKey = new Map(hand.map((c) => [cardKey(c), c]));
            const unpinned = positions.filter((k) => byKey.has(k) && !pinnedKeys.has(k));
            const sortedUnpinned = unpinned
                .map((k) => byKey.get(k))
                .sort((a, b) => {
                    if (mode === 'suit') {
                        const su = suitOrder[a[1]] - suitOrder[b[1]];
                        return su !== 0 ? su : rankValue(a) - rankValue(b);
                    }
                    const rv = rankValue(a) - rankValue(b);
                    return rv !== 0 ? rv : suitOrder[a[1]] - suitOrder[b[1]];
                })
                .map(cardKey);
            // Walk the original slot list; keep pinned in place, drop sorted
            // unpinned into the freed slots in order.
            let idx = 0;
            const result = positions.map((k) => {
                if (pinnedKeys.has(k) && byKey.has(k)) return k;
                const next = sortedUnpinned[idx++];
                return next ?? k;
            });
            // Ensure every server card is represented (defensive).
            for (const c of hand) {
                const k = cardKey(c);
                if (!result.includes(k)) result.push(k);
            }
            return result;
        });
        setSwapIdx(null);
    }, [mySeat?.hand, pinnedKeys]);

    const togglePin = useCallback((card) => {
        const k = cardKey(card);
        setPinnedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
        // Any pin toggle also cancels an in-flight tap-swap.
        setSwapIdx(null);
    }, []);

    // Tap-swap: tap first card to lift, tap second to swap positions.
    const beginOrSwap = useCallback((idx) => {
        setSwapIdx((cur) => {
            if (cur == null) return idx;
            if (cur === idx) return null; // tapped same card → cancel
            setHandOrder((prev) => {
                const next = prev.slice();
                [next[cur], next[idx]] = [next[idx], next[cur]];
                return next;
            });
            return null;
        });
    }, []);

    // Force a fresh table snapshot from the server. Used to self-heal
    // whenever we detect stale local state (e.g. an action or start-hand
    // call fails because the phase moved on without us noticing).
    const refreshTable = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `${API}/tonk/tables/${tableId}`,
                { withCredentials: true },
            );
            setTable(data);
        } catch { /* keep old state */ }
    }, [tableId]);
    // Expose refreshTable to earlier-declared effects (turn-timer
    // self-heal) via a ref to sidestep the TDZ.
    refreshTableRef.current = refreshTable;

    // ─── Actions ────────────────────────────────────────────────────────
    const sendAction = useCallback(async (payload) => {
        // De-dup rapid double-taps: signature = action + card key. Second
        // tap while the first is in flight is a no-op so we don't fire
        // "discard 4H" twice and get "Not your turn" on the follow-up.
        const sig = `${payload.action}:${payload.card ? cardKey(payload.card) : ''}`;
        if (inFlightActionRef.current === sig) return;
        inFlightActionRef.current = sig;
        setActionInFlight(true);
        // Optimistic UI — if this is a discard, hide the card from the
        // local hand IMMEDIATELY so tapping feels instantaneous. The WS
        // state broadcast (100-300ms later) is the source of truth; if
        // the server rejects, we revert below.
        const optimisticKey = payload.action === 'discard' && payload.card ? cardKey(payload.card) : null;
        if (optimisticKey) {
            setPendingRemoveKeys((prev) => {
                const n = new Set(prev);
                n.add(optimisticKey);
                return n;
            });
        }
        // Every action gets a small haptic pulse so the phone confirms
        // the tap registered even if the server is slow. Critical UX on
        // iOS Safari where a laggy WS reply can otherwise feel frozen.
        haptic(15);
        try {
            const { data: actionResp } = await axios.post(
                `${API}/tonk/tables/${tableId}/actions`,
                payload,
                {
                    withCredentials: true,
                    // Hard cap so a hung mobile connection can't jam the
                    // in-flight lock forever. If the server hasn't
                    // responded in 8s we surface a retry-able error
                    // rather than freezing the UI indefinitely.
                    timeout: 8000,
                },
            );
            // Apply the server-authoritative state RIGHT AWAY so the
            // player doesn't have to wait on the WS broadcast for their
            // own move to appear. Huge perceived-speed win on mobile.
            if (actionResp?.table) {
                setTable(actionResp.table);
            }
        } catch (err) {
            const detail = String(err?.response?.data?.detail || '');
            const isStaleTurn = /not your turn|already dealt|hand already|already ended/i.test(detail);
            const isTimeout = err?.code === 'ECONNABORTED';
            // Revert the optimistic hide on ANY error so the card comes back.
            if (optimisticKey) {
                setPendingRemoveKeys((prev) => {
                    const n = new Set(prev);
                    n.delete(optimisticKey);
                    return n;
                });
            }
            if (isTimeout) {
                toast.error("Connection is slow — tap again to retry.");
                refreshTable();
            } else if (isStaleTurn) {
                refreshTable();
            } else {
                toast.error(detail || 'Illegal move');
                refreshTable();
            }
        } finally {
            inFlightActionRef.current = null;
            setActionInFlight(false);
        }
    }, [tableId, refreshTable]);

    // Once the server broadcast updates `mySeat.hand` and the card is
    // truly gone, drop it from `pendingRemoveKeys` too. This also cleans
    // up any leftover keys if the server draws a fresh hand mid-game.
    useEffect(() => {
        const live = new Set((mySeat?.hand || []).map(cardKey));
        setPendingRemoveKeys((prev) => {
            if (prev.size === 0) return prev;
            const kept = new Set([...prev].filter((k) => live.has(k)));
            return kept.size === prev.size ? prev : kept;
        });
    }, [mySeat?.hand]);

    // Extra safety net: whenever the hand number ticks (a fresh deal),
    // wipe pendingRemoveKeys unconditionally. This closes a nasty race:
    // if a discard action ALSO ends the hand and the new deal happens
    // to re-deal the same rank/suit back to us, the effect above would
    // KEEP that stale key (because it's `live` again), causing the card
    // to be hidden forever until the next natural hand transition.
    // Reported by user (Iter 271): "only got 4 cards after playing a
    // few rounds" — visible hand summed to 24 but pts said 34, meaning
    // one 10-value card was being filtered out.
    useEffect(() => {
        setPendingRemoveKeys((prev) => (prev.size === 0 ? prev : new Set()));
    }, [table?.hand_number]);

    // Send a reaction over the socket. Falls back silently if the WS is
    // reconnecting — reactions are ephemeral, no point queueing them.
    const sendReaction = useCallback((emoji) => {
        const now = Date.now();
        if (now - lastReactionAtRef.current < 900) return; // client-side rate limit
        lastReactionAtRef.current = now;
        const ws = wsRef.current;
        if (!ws || ws.readyState !== 1) return;
        try {
            ws.send(JSON.stringify({ type: 'reaction', emoji }));
        } catch { /* drop */ }
        setReactionOpen(false);
    }, []);

    // Latest chat bubble per seat_index → rendered in TonkFelt. Suppress
    // messages from muted senders (per-viewer only — the sender still
    // sees their own bubble). Reactions get the same treatment.
    const visibleChatMsgs = useMemo(() => {
        if (mutedKeys.size === 0) return chatMsgs;
        return chatMsgs.filter((m) => {
            const seat = (table?.seats || [])[m.seat];
            const key = muteKeyForSeat(seat);
            return !key || !mutedKeys.has(key);
        });
    }, [chatMsgs, mutedKeys, table?.seats, muteKeyForSeat]);
    const visibleReactions = useMemo(() => {
        if (mutedKeys.size === 0) return reactions;
        return reactions.filter((r) => {
            const seat = (table?.seats || [])[r.seat];
            const key = muteKeyForSeat(seat);
            return !key || !mutedKeys.has(key);
        });
    }, [reactions, mutedKeys, table?.seats, muteKeyForSeat]);
    // limit and a 120-char cap; we mirror those here to keep the UI honest.
    const sendChat = useCallback((rawText) => {
        const text = String(rawText || '').trim().slice(0, 120);
        if (!text) return;
        const now = Date.now();
        if (now - lastChatAtRef.current < 1500) {
            toast('Slow down — one message every 1.5s', { icon: '\u23f3' });
            return;
        }
        lastChatAtRef.current = now;
        const ws = wsRef.current;
        if (!ws || ws.readyState !== 1) {
            toast.error('Chat is offline — reconnecting');
            return;
        }
        try {
            ws.send(JSON.stringify({ type: 'chat', text }));
            setChatDraft('');
            setChatOpen(false);
        } catch { /* drop */ }
    }, []);

    const startHand = async () => {
        setStarting(true);
        try {
            await axios.post(`${API}/tonk/tables/${tableId}/start`, {}, { withCredentials: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Could not start';
            toast.error(detail);
            // "Hand already in progress" means our WS missed the update.
            // Force a fresh snapshot so the UI catches up instead of
            // showing DEAL HAND indefinitely.
            refreshTable();
        } finally {
            setStarting(false);
        }
    };

    // Safety net: while the WebSocket is DOWN, poll the REST snapshot
    // every 3 seconds so the UI can't get permanently stranded showing
    // stale phase / turn / hand data. Bails immediately once the socket
    // comes back live (that's where real-time updates come from).
    // 3s (was 8s in iter 141) is aggressive but cheap and matches iOS
    // wake-up + Cloudflare reconnect timing.
    useEffect(() => {
        if (wsLive) return undefined;
        const id = setInterval(() => { refreshTable(); }, 3000);
        return () => clearInterval(id);
    }, [wsLive, refreshTable]);

    const leaveTable = async () => {
        // Warn before mid-hand leaves on real-money tables — the ante
        // stays in the pot, but no more chips are debited after.
        const midHand = table?.phase === 'in_hand' && !mySeat?.has_dropped && !mySeat?.is_out;
        const isReal = table?.mode === 'real';
        if (midHand && isReal) {
            const ante = Number(table?.rules?.entry_amount || 0).toFixed(2);
            const ok = window.confirm(`Leave now? Your $${ante} ante stays in the pot, and no more will be debited. You can rejoin later.`);
            if (!ok) return;
        }
        try {
            await axios.post(`${API}/tonk/tables/${tableId}/leave`, {}, { withCredentials: true });
        } catch { /* ignore */ }
        nav('/casino/tonk');
    };

    // ─── Take-a-seat (invite arrival) ────────────────────────────────
    // A brand-new invitee lands at /casino/tonk/{id} still un-seated.
    // Auto-attempt to join if a seat is open. On 402 (real-money table +
    // low wallet) prompt them to load funds via Profile. On 403 (private
    // join code) prompt for the code.
    const [joining, setJoining] = useState(false);
    const [needsFunds, setNeedsFunds] = useState(null); // { needed, mode }
    const takeSeat = useCallback(async (opts = {}) => {
        if (joining) return;
        setJoining(true);
        try {
            const body = opts.code ? { join_code: opts.code } : {};
            const midHandJoin = table?.phase === 'in_hand';
            await axios.post(`${API}/tonk/tables/${tableId}/join`, body, { withCredentials: true });
            if (midHandJoin) {
                toast.success("You're seated — cards deal at the start of the next hand.");
            } else {
                toast.success("You're seated — good luck.");
            }
            setNeedsFunds(null);
            refreshTable();
        } catch (err) {
            const st = err?.response?.status;
            const detail = err?.response?.data?.detail || 'Could not take a seat';
            if (st === 402) {
                // Not enough wallet — surface a load-funds prompt inline
                const needed = Number(table?.rules?.entry_amount || 0);
                setNeedsFunds({ needed, mode: table?.mode || 'real' });
                toast.error(detail);
            } else if (st === 403) {
                const code = window.prompt('This table needs a join code:');
                if (code) return takeSeat({ code });
            } else {
                toast.error(detail);
            }
        } finally {
            setJoining(false);
        }
    }, [joining, tableId, table?.rules?.entry_amount, table?.mode, refreshTable]);

    // ─── Auto-rejoin (?rejoin=1) ─────────────────────────────────────
    // The auto-drop toast's "Rejoin" button routes here with `?rejoin=1`.
    // If the table has an open seat and the user isn't already seated,
    // silently claim one. If it's full, takeSeat will surface a toast
    // and the user stays as a spectator. Fires only ONCE per mount.
    const rejoinAttempted = useRef(false);
    useEffect(() => {
        if (rejoinAttempted.current) return;
        if (searchParams.get('rejoin') !== '1') return;
        if (!table) return;                       // wait for the table snapshot
        if (mySeat) {                             // already seated → nothing to do
            rejoinAttempted.current = true;
            return;
        }
        const hasOpenSeat = (table.seats || []).some((s) => !s.user_id && !s.is_bot);
        if (!hasOpenSeat) {
            rejoinAttempted.current = true;
            toast.info('This table is full — sit back and spectate for now.');
            return;
        }
        rejoinAttempted.current = true;
        takeSeat();
    }, [searchParams, table, mySeat, takeSeat]);

    // ─── Copy invite link ────────────────────────────────────────────
    // Deep-link that ProtectedRoute already turns into an auto-return
    // login/signup flow (see /app/frontend/src/components/ProtectedRoute.js).
    // Uses Web Share API if the browser supports it (mobile-friendly),
    // else copies to clipboard.
    const copyInviteLink = useCallback(async () => {
        const url = `${window.location.origin}/casino/tonk/${tableId}`;
        const text = `Join me at "${table?.name || 'my table'}" on BETohalics — Good Ol' Tonk`;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'BETohalics · Tonk', text, url });
                return;
            }
            await navigator.clipboard.writeText(url);
            toast.success('Invite link copied — paste it into any chat');
        } catch (err) {
            if (err?.name === 'AbortError') return; // user cancel
            // Last-ditch: prompt so they can copy manually.
            try {
                window.prompt('Copy this invite link:', url);
            } catch { /* ignore */ }
        }
    }, [tableId, table?.name]);

    const toggleSelectCard = (card) => {
        if (!isMyTurn) return;
        setSelected((cur) => {
            const key = cardKey(card);
            const idx = cur.findIndex((c) => cardKey(c) === key);
            if (idx >= 0) return cur.filter((_, i) => i !== idx);
            return [...cur, card];
        });
    };

    const discardSelected = async () => {
        if (selected.length !== 1) {
            toast.error('Select exactly one card to discard');
            return;
        }
        await sendAction({ action: 'discard', card: selected[0] });
    };

    const spreadSelected = async () => {
        if (selected.length < 3) {
            toast.error('Spreads need 3+ cards');
            return;
        }
        await sendAction({ action: 'spread', cards: selected });
    };

    const drop = async () => {
        await sendAction({ action: 'drop' });
    };

    const tonkOut = async () => {
        await sendAction({ action: 'tonk_out' });
    };

    // ─── Smart move detection ──────────────────────────────────────────
    // Scan my hand for the largest legal spread (biggest set OR longest run).
    // Runs assume ace-low; if the table's ace_high_runs rule is on we also
    // let A extend K→Q→J high. Returns { cards: Card[], label: string } or null.
    const bestSpread = useMemo(() => {
        if (!mySeat || !(mySeat.hand?.length >= 3)) return null;
        const aceHigh = !!table?.rules?.ace_high_runs;
        const hand = mySeat.hand;

        // 1) Sets — group by rank
        const byRank = {};
        for (const c of hand) {
            (byRank[c[0]] = byRank[c[0]] || []).push(c);
        }
        let best = null;
        for (const [rankStr, cards] of Object.entries(byRank)) {
            if (cards.length >= 3) {
                const rank = Number(rankStr);
                if (!best || cards.length > best.cards.length) {
                    best = { cards, label: `${cards.length}× ${RANK_STR[rank] || rank}` };
                }
            }
        }
        // 2) Runs — per suit, find longest consecutive
        for (const suit of ['S', 'H', 'D', 'C']) {
            const suited = hand.filter((c) => c[1] === suit);
            if (suited.length < 3) continue;
            // Rank map with ace-high copy
            const ranks = [...new Set(suited.map((c) => c[0]))].sort((a, b) => a - b);
            if (aceHigh && ranks.includes(1)) ranks.push(14);
            ranks.sort((a, b) => a - b);
            // Walk consecutive
            let run = [ranks[0]];
            let longest = [ranks[0]];
            for (let i = 1; i < ranks.length; i += 1) {
                if (ranks[i] === ranks[i - 1] + 1) {
                    run.push(ranks[i]);
                } else {
                    if (run.length > longest.length) longest = [...run];
                    run = [ranks[i]];
                }
            }
            if (run.length > longest.length) longest = [...run];
            if (longest.length >= 3) {
                const runCards = longest.map((r) => {
                    const originalRank = r === 14 ? 1 : r;
                    return suited.find((c) => c[0] === originalRank);
                }).filter(Boolean);
                if (!best || runCards.length > best.cards.length) {
                    const lo = RANK_STR[runCards[0][0]] || runCards[0][0];
                    const hi = RANK_STR[runCards[runCards.length - 1][0]] || runCards[runCards.length - 1][0];
                    best = { cards: runCards, label: `${lo}${SUIT_CHAR[suit]}\u2192${hi}${SUIT_CHAR[suit]}` };
                }
            }
        }
        return best;
    }, [mySeat, table?.rules?.ace_high_runs]);

    // Auto-lay the best detected spread with one tap.
    const layBestSpread = async () => {
        if (!bestSpread) return;
        await sendAction({ action: 'spread', cards: bestSpread.cards });
    };

    // Enumerate all legal hits I can make on any laid spread on the felt.
    // A hit is legal when a card in my hand extends a spread — same rank
    // for sets, adjacent (or ace-high) rank for runs. Returns list of
    // {seat, spread_index, card, label} shaped for the send API.
    const availableHits = useMemo(() => {
        if (!table || !mySeat || !mySeat.hand?.length) return [];
        if (!hasDrawn) return []; // must draw before hitting
        const aceHigh = !!table.rules?.ace_high_runs;
        const canOthers = table.rules?.can_hit_others !== false;
        const canOwn = !!table.rules?.can_hit_own;
        const hits = [];
        for (const seat of table.seats) {
            if (seat.seat_index === mySeat.seat_index && !canOwn) continue;
            if (seat.seat_index !== mySeat.seat_index && !canOthers) continue;
            const spreads = seat.spreads || [];
            spreads.forEach((sp, spreadIdx) => {
                if (sp.length === 0) return;
                const ranks = sp.map((c) => c[0]);
                const suit = sp[0][1];
                const allSameRank = ranks.every((r) => r === ranks[0]);
                const allSameSuit = sp.every((c) => c[1] === suit);
                for (const myC of mySeat.hand) {
                    if (allSameRank && myC[0] === ranks[0]) {
                        hits.push({
                            target_seat: seat.seat_index,
                            spread_index: spreadIdx,
                            card: myC,
                            label: `${RANK_STR[myC[0]] || myC[0]}${SUIT_CHAR[myC[1]]} on ${seat.display_name?.split(' ')[0] || 'seat'}`,
                        });
                    } else if (!allSameRank && allSameSuit && myC[1] === suit) {
                        const sortedRanks = [...ranks].sort((a, b) => a - b);
                        let lo = sortedRanks[0];
                        let hi = sortedRanks[sortedRanks.length - 1];
                        if (aceHigh && sortedRanks.includes(1)) {
                            // Ace-high case: 1 acts as 14
                            hi = Math.max(hi, 14);
                        }
                        // Extend by 1 either end
                        const cardRank = myC[0] === 1 && aceHigh && hi < 13 ? 1 : myC[0];
                        const cardHigh = myC[0] === 1 && aceHigh ? 14 : myC[0];
                        if (cardHigh === hi + 1 || cardRank === lo - 1) {
                            hits.push({
                                target_seat: seat.seat_index,
                                spread_index: spreadIdx,
                                card: myC,
                                label: `${RANK_STR[myC[0]] || myC[0]}${SUIT_CHAR[myC[1]]} on ${seat.display_name?.split(' ')[0] || 'seat'}`,
                            });
                        }
                    }
                }
            });
        }
        return hits;
    }, [table, mySeat, hasDrawn]);

    const hitOne = async (h) => {
        await sendAction({
            action: 'hit',
            target_seat: h.target_seat,
            spread_index: h.spread_index,
            card: h.card,
        });
    };

    // Suggestions: given the currently selected cards, which OTHER cards in
    // my hand could extend the selection toward a legal spread? Returns a
    // Set of cardKey strings. Empty when nothing is selected.
    const spreadSuggestions = useMemo(() => {
        const out = new Set();
        if (!mySeat || selected.length === 0) return out;
        const hand = mySeat.hand || [];
        for (const c of hand) {
            const key = cardKey(c);
            if (selected.some((sc) => cardKey(sc) === key)) continue;
            const trial = [...selected, c];
            // As a SET: all same rank
            const setOk = trial.every((x) => x[0] === trial[0][0]);
            let runOk = false;
            const suit = trial[0][1];
            if (trial.every((x) => x[1] === suit)) {
                const ranks = trial.map((x) => x[0]);
                const uniq = new Set(ranks);
                if (uniq.size === ranks.length) {
                    const sortedR = [...ranks].sort((a, b) => a - b);
                    const span = sortedR[sortedR.length - 1] - sortedR[0];
                    runOk = span <= 5; // room to still form a valid run
                }
            }
            if (setOk || runOk) out.add(key);
        }
        return out;
    }, [mySeat, selected]);

    // Highlight laid spreads on the felt that any selected card can hit.
    // Returns a Set of "seatIndex-spreadIndex" strings for the felt to
    // outline in gold. Only considers cards actually selected right now.
    const hittableSpreadKeys = useMemo(() => {
        const out = new Set();
        if (!table || !mySeat || selected.length === 0) return out;
        if (!hasDrawn) return out;
        for (const h of availableHits) {
            if (selected.some((sc) => cardKey(sc) === cardKey(h.card))) {
                out.add(`${h.target_seat}-${h.spread_index}`);
            }
        }
        return out;
    }, [table, mySeat, selected, hasDrawn, availableHits]);

    // ─── Render ─────────────────────────────────────────────────────────
    if (!table) {
        return (
            <div className="min-h-screen text-white flex items-center justify-center" style={{ background: THEME.pageBg }}>
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    const discardTop = table.discard_pile?.length ? table.discard_pile[table.discard_pile.length - 1] : null;
    const activeSeats = table.seats.filter((s) => s.user_id || s.is_bot);
    const opponents = table.seats.filter((s) => s.seat_index !== mySeat?.seat_index);
    const currentTurnSeat = table.seats[table.turn_index];
    const canStart = table.phase === 'waiting' && mySeat && (String(table.host_user_id) === String(user?.id));

    return (
        <div className="a11y-font min-h-screen text-white relative overflow-hidden" style={{ background: THEME.pageBg }} data-testid="tonk-room">
            <Header />

            {/* Top bar */}
            <div className="max-w-3xl mx-auto px-4 pt-3 pb-2 flex items-center justify-between">
                <button
                    onClick={leaveTable}
                    className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
                    data-testid="tonk-leave-btn"
                >
                    <ArrowLeft className="w-4 h-4" /> Leave
                </button>
                <div className="flex flex-col items-center text-center">
                    <span
                        className="text-lg font-black tracking-wider"
                        style={{ fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                        data-testid="tonk-table-name"
                    >
                        {table.name}
                    </span>
                    <span className="text-[10px] font-mono text-white/50">
                        Hand #{table.hand_number} · Ante {fmt$(table.rules?.entry_amount)} · {(table.mode || '').toUpperCase()}
                    </span>
                    {/* Wallet chip — persistent balance so the player never
                        has to leave the table to check what they've got.
                        Feature request 2026-07-22: "put the wallet amount
                        on the screen so the player can see it at all
                        times." Lit up when your balance is running low
                        so you notice before the next ante. */}
                    {user?.credits != null && (
                        <span
                            className={`mt-0.5 inline-flex items-center gap-1 text-[11px] font-mono font-bold px-2 py-0.5 rounded-full border ${
                                playable(user) < Number(table.rules?.entry_amount || 0) * 3
                                    ? 'text-red-300 border-red-400/60 bg-red-500/10'
                                    : 'text-[#F5C542] border-[#F5C542]/40 bg-[#F5C542]/10'
                            }`}
                            data-testid="tonk-wallet-chip"
                            title="Your live wallet balance"
                        >
                            <Wallet className="w-3 h-3" />
                            {fmt$(playable(user))}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1 text-[10px] font-mono" title={wsLive ? 'Live' : 'Reconnecting'}>
                    <button
                        onClick={() => {
                            const nm = !muted;
                            setMuted(nm);
                            setMutedState(nm);
                            if (!nm) playFlick(0.2); // audible cue on unmute
                        }}
                        className="mr-1 p-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 transition"
                        title={muted ? 'Enable sound' : 'Mute sound'}
                        aria-label={muted ? 'Enable sound' : 'Mute sound'}
                        data-testid="tonk-mute-toggle"
                    >
                        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={() => setReactionOpen((v) => !v)}
                        className="mr-1 p-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 transition"
                        data-testid="tonk-react-toggle"
                        aria-label="Send a reaction"
                    >
                        <Smile className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => { setChatOpen((v) => !v); setReactionOpen(false); }}
                        className="mr-1 p-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 transition"
                        data-testid="tonk-chat-toggle"
                        aria-label="Open chat"
                    >
                        <MessageCircle className="w-4 h-4" />
                    </button>
                    <button
                        onClick={copyInviteLink}
                        className="mr-1 p-1.5 rounded-full border border-[#F5C542]/40 bg-[#F5C542]/10 hover:bg-[#F5C542]/20 text-[#F5C542] transition"
                        data-testid="tonk-invite-btn"
                        aria-label="Copy invite link"
                        title="Copy invite link to share"
                    >
                        <Share2 className="w-4 h-4" />
                    </button>
                    {(() => {
                        // Connection-quality badge. Combines wsLive with
                        // last-pong freshness and measured ping latency
                        // to give mom a live signal of her network
                        // before she taps.
                        const now = Date.now();
                        const staleFor = lastPongMs ? (now - lastPongMs) : Infinity;
                        let cls = 'text-green-400';
                        let label = 'LIVE';
                        let icon = <Wifi className="w-3.5 h-3.5" />;
                        if (!wsLive || staleFor > 6000) {
                            cls = 'text-red-400';
                            label = 'RECONNECT';
                            icon = <WifiOff className="w-3.5 h-3.5" />;
                        } else if (staleFor > 2500 || (pingMs != null && pingMs > 800)) {
                            cls = 'text-amber-400';
                            label = 'SLOW';
                        }
                        return (
                            <div
                                className={`flex items-center gap-1 ${cls}`}
                                title={pingMs != null ? `${pingMs} ms round-trip` : (wsLive ? 'Live' : 'Reconnecting')}
                                data-testid="tonk-conn-quality"
                            >
                                {icon}
                                <span className="text-white/50">{label}</span>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Reactions rail — appears under the top bar when opened */}
            <AnimatePresence>
                {reactionOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="max-w-3xl mx-auto px-4 -mt-1 mb-1 flex justify-end gap-1"
                        data-testid="tonk-reactions-rail"
                    >
                        {Object.entries(REACTIONS).map(([code, r]) => (
                            <button
                                key={code}
                                onClick={() => sendReaction(code)}
                                className="w-10 h-10 rounded-full bg-black/70 border border-white/15 text-xl leading-none hover:bg-white/10 hover:scale-110 transition"
                                title={r.label}
                                aria-label={r.label}
                                data-testid={`tonk-react-${code}`}
                            >
                                <span aria-hidden="true">{r.char}</span>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Chat rail — text input row + quick pills */}
            <AnimatePresence>
                {chatOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="max-w-3xl mx-auto px-4 -mt-1 mb-1 space-y-1"
                        data-testid="tonk-chat-rail"
                    >
                        {/* Quick pills — tap to send without typing. Curated
                            short lines that stay warm/PG. */}
                        {mySeat && (
                            <div className="flex gap-1 overflow-x-auto no-scrollbar">
                                {['GL', 'nice', 'oof', 'my turn ?', 'lucky', 'gg', 'watch me', 'tough', 'lets go', 'clean it'].map((phrase) => (
                                    <button
                                        key={phrase}
                                        type="button"
                                        onClick={() => sendChat(phrase)}
                                        className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-white/15 bg-black/60 text-white/80 hover:border-[#F5C542]/60 hover:text-[#F5C542] transition"
                                        data-testid={`tonk-chat-pill-${phrase.replace(/\W+/g, '-')}`}
                                    >
                                        {phrase}
                                    </button>
                                ))}
                            </div>
                        )}
                        <form
                            onSubmit={(e) => { e.preventDefault(); sendChat(chatDraft); }}
                            className="flex items-center gap-2 rounded-full border border-white/15 bg-black/70 px-3 py-1.5"
                        >
                            <MessageCircle className="w-4 h-4 text-white/50 shrink-0" />
                            <input
                                type="text"
                                value={chatDraft}
                                onChange={(e) => setChatDraft(e.target.value.slice(0, 120))}
                                placeholder={mySeat ? 'Say something\u2026' : 'Take a seat to chat'}
                                disabled={!mySeat}
                                maxLength={120}
                                autoFocus
                                className="flex-1 bg-transparent text-white text-sm placeholder:text-white/30 outline-none disabled:opacity-50"
                                data-testid="tonk-chat-input"
                            />
                            <button
                                type="submit"
                                disabled={!mySeat || !chatDraft.trim()}
                                className="p-1.5 rounded-full text-[#F5C542] hover:bg-white/10 disabled:opacity-30 transition"
                                aria-label="Send chat"
                                data-testid="tonk-chat-send"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Recent winners strip — last 5 hands as tappable chips */}
            {recentHands.length > 0 && (
                <div className="max-w-3xl mx-auto px-4 -mt-1 mb-1" data-testid="tonk-recent-strip">
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
                        <span className="text-[9px] uppercase tracking-widest text-white/40 shrink-0 pr-1"
                              style={{ fontFamily: THEME.headingFont, letterSpacing: '0.12em' }}>
                            Recent
                        </span>
                        {recentHands.map((h, idx) => {
                            const winnerSeat = table.seats?.[h.winner_seat];
                            const winnerName = h.winner_user_id === user?.id
                                ? 'You'
                                : (winnerSeat?.display_name || `Seat ${h.winner_seat}`);
                            const isMe = h.winner_user_id === user?.id;
                            const kindTag = (h.kind || 'HAND').toUpperCase().replace('_', ' ');
                            return (
                                <div
                                    key={`${h.hand_number ?? idx}-${h.ended_at ?? idx}`}
                                    className="shrink-0 flex items-center gap-1.5 rounded-full border px-2 py-1 text-[10px]"
                                    style={{
                                        borderColor: isMe ? THEME.accent : 'rgba(255,255,255,0.15)',
                                        background: isMe ? 'rgba(245,197,66,0.10)' : 'rgba(0,0,0,0.35)',
                                    }}
                                    data-testid={`tonk-recent-chip-${idx}`}
                                    title={`Hand #${h.hand_number} — ${kindTag}`}
                                >
                                    <Trophy className="w-3 h-3" style={{ color: THEME.accent }} />
                                    <span className={`font-mono ${isMe ? 'text-[#F5C542]' : 'text-white/80'}`}>
                                        {winnerName}
                                    </span>
                                    <span className="text-white/40">·</span>
                                    <span className="text-white/70">{kindTag}</span>
                                    {h.multiplier > 1 && (
                                        <span className="ml-0.5 text-[#F5C542] font-bold">{h.multiplier}×</span>
                                    )}
                                    <span className="text-white/40">·</span>
                                    <span className="text-[#F5C542] font-mono font-bold">{fmt$(h.payout)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="max-w-3xl mx-auto px-4 pb-32">
                {/* Square felt table with players on each side */}
                <TonkFelt
                    seats={table.seats}
                    mySeatIndex={mySeat?.seat_index ?? -1}
                    myAvatar={myAvatar}
                    turnIndex={table.turn_index}
                    dealerIndex={table.dealer_index}
                    chatMsgs={visibleChatMsgs}
                    onMuteSeat={(seat) => toggleMute(muteKeyForSeat(seat))}
                    onInvite={copyInviteLink}
                    mutedKeys={mutedKeys}
                    muteKeyForSeat={muteKeyForSeat}
                    winnerSeatIndex={
                        table.phase === 'hand_ended'
                            ? (table.last_hand_result?.winner_seat
                                ?? table.winner_index_last_hand
                                ?? handEndBanner?.winner_seat
                                ?? -1)
                            : -1
                    }
                    phase={table.phase}
                    turnFrac={turnFrac}
                    hittableSpreadKeys={hittableSpreadKeys}
                    timerSecs={
                        table.phase === 'in_hand'
                            ? Math.max(0, Math.ceil((table.rules?.turn_timer_seconds || 20) * turnFrac))
                            : null
                    }
                >
                    {/* Center piles + status. Rendered as a child so it sits
                        inside the felt's grid without messing with layout. */}
                    <div className="w-full">
                        {/* Reactions overlay — stacks in the top-right of the center cell */}
                        <div className="absolute right-1 top-1 pointer-events-none flex flex-col items-end gap-1 z-20" data-testid="tonk-reaction-stream">
                            <AnimatePresence>
                                {visibleReactions.map((r) => (
                                    <motion.div
                                        key={r.id}
                                        initial={{ opacity: 0, x: 20, scale: 0.6 }}
                                        animate={{ opacity: 1, x: 0, scale: 1 }}
                                        exit={{ opacity: 0, x: 20, scale: 0.6 }}
                                        transition={{ duration: 0.35, ease: 'easeOut' }}
                                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-2xl bg-black/85 border border-[#F5C542]/40 shadow-[0_0_12px_rgba(245,197,66,0.25)] ${r.line ? 'max-w-[200px]' : ''}`}
                                    >
                                        <span aria-hidden="true" className="text-lg leading-none shrink-0">{REACTIONS[r.emoji]?.char}</span>
                                        <div className="flex flex-col leading-tight min-w-0">
                                            <span className="text-[11px] font-bold text-white/90 truncate">{r.name}</span>
                                            {r.line && (
                                                <span className="text-[11px] italic text-white/75 truncate" data-testid="tonk-reaction-line">
                                                    &ldquo;{r.line}&rdquo;
                                                </span>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        <div className="flex items-center justify-center gap-4">
                            <button
                                className="flex flex-col items-center gap-1 disabled:opacity-50"
                                onClick={() => sendAction({ action: 'draw', source: 'deck' })}
                                disabled={!isMyTurn || hasDrawn || (table.draw_pile_count || 0) <= 0}
                                data-testid="tonk-draw-deck-btn"
                            >
                                <div className="relative">
                                    <CardBack />
                                    <span className="absolute -bottom-1 -right-1 text-[9px] font-mono bg-black/60 px-1 rounded">
                                        {table.draw_pile_count ?? 0}
                                    </span>
                                </div>
                                <span className="text-[10px] text-white/70">DECK</span>
                            </button>

                            <div className="flex flex-col items-center">
                                <span className="text-[10px] text-white/50 tracking-widest">POT</span>
                                <span
                                    className="text-2xl sm:text-3xl font-black leading-none"
                                    style={{ color: THEME.accent, fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter }}
                                    data-testid="tonk-pot-amount"
                                >
                                    {fmt$(table.pot || 0)}
                                </span>
                                <span className="text-[9px] text-white/50">
                                    {activeSeats.length}/{table.rules?.max_players || 5} seated
                                </span>
                            </div>

                            <SwipableDiscard
                                card={discardTop}
                                enabled={!!(isMyTurn && !hasDrawn && discardTop)}
                                onDraw={() => sendAction({ action: 'draw', source: 'discard' })}
                            />
                        </div>

                        <div className="mt-3 text-center text-[11px] text-white/70 font-mono">
                            {table.phase === 'waiting' && 'Waiting for host to deal…'}
                            {table.phase === 'in_hand' && (
                                isMyTurn
                                    ? <span className="text-[#F5C542] font-bold">YOUR TURN — {hasDrawn ? 'discard or spread' : 'draw a card'}</span>
                                    : <span>{currentTurnSeat?.display_name || 'Bot'}&rsquo;s turn…</span>
                            )}
                            {table.phase === 'hand_ended' && (
                                nextHandSecs != null && nextHandSecs > 0
                                    ? <span className="text-[#F5C542] font-bold">Next hand in {nextHandSecs}s…</span>
                                    : 'Hand ended. Dealing next hand…'
                            )}
                        </div>
                    </div>
                </TonkFelt>

                {/* My spreads — shown just below the felt so you can hit them */}
                {mySeat && mySeat.spreads?.length > 0 && (
                    <div className="mt-3 rounded-xl border border-white/10 bg-black/40 p-2">
                        <p className="text-[10px] text-white/60 tracking-widest mb-1">YOUR SPREADS</p>
                        <div className="flex flex-wrap gap-2">
                            {mySeat.spreads.map((sp, si) => {
                                const hittable = hittableSpreadKeys.has(`${mySeat.seat_index}-${si}`);
                                return (
                                    <div
                                        key={si}
                                        className={`flex gap-0.5 rounded px-1 py-0.5 transition ${hittable ? 'border-2 border-[#F5C542] shadow-[0_0_10px_rgba(245,197,66,0.6)]' : ''}`}
                                        data-testid={`tonk-my-spread-${si}${hittable ? '-hittable' : ''}`}
                                    >
                                        {sp.map((c) => <CardFace key={cardKey(c)} card={c} small />)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom sheet: my hand + actions.
                User feedback 2026-07-22: "the black screen at the bottom
                looks so cut off." Switched from bg-black/95 to a
                bottom-up gradient that fades from transparent felt-green
                to a dark base — keeps the action bar legible while
                letting the felt bleed through visually. */}
            <div
                className="fixed bottom-0 left-0 right-0 px-3 pt-3 z-40"
                style={{
                    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(6,60,44,0.55) 22%, rgba(0,0,0,0.85) 75%, rgba(0,0,0,0.95) 100%)',
                    borderTop: '1px solid rgba(245,197,66,0.12)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                }}
            >
                <div className="max-w-3xl mx-auto">
                    {mySeat ? (
                        <>
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <button
                                            onClick={() => setAvatarOpen(true)}
                                            className="w-7 h-7 rounded-full overflow-hidden border border-white/25 bg-black/40 flex items-center justify-center hover:border-[#F5C542]/60 transition"
                                            title="Change your look"
                                            data-testid="tonk-open-avatar-picker"
                                        >
                                            {myAvatar.kind ? (
                                                <img
                                                    src={
                                                        myAvatar.kind === 'preset'
                                                            ? `https://api.dicebear.com/9.x/avataaars/svg?seed=${encodeURIComponent(myAvatar.value)}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffdfbf,ffd5dc`
                                                            : myAvatar.value
                                                    }
                                                    alt="me"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <UserIcon className="w-4 h-4 text-white/60" />
                                            )}
                                        </button>
                                        {mySeat?.seat_index === table.dealer_index && (table.phase === 'in_hand' || table.phase === 'hand_ended') && (
                                            <span
                                                className="absolute -top-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black border border-white/80"
                                                style={{ background: '#FDFCF4', color: '#000' }}
                                                title="You deal this hand"
                                                data-testid="tonk-my-dealer-chip"
                                            >
                                                D
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-[10px] text-white/60 tracking-widest">YOUR HAND</span>
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/10 text-white/80" data-testid="tonk-hand-total">
                                        {handTotal(mySeat.hand)} pts
                                    </span>
                                    {isMyTurn && (
                                        <span className="text-[9px] font-mono text-[#F5C542] flex items-center gap-1">
                                            <Clock className="w-3 h-3" /> {Math.max(0, Math.ceil((table.rules?.turn_timer_seconds || 20) * turnFrac))}s
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {/* Personal sort buttons — no server sync, purely visual */}
                                    {(mySeat.hand?.length || 0) > 1 && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => sortHandBy('rank')}
                                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-white/15 text-white/70 hover:text-[#F5C542] hover:border-[#F5C542]/60 transition"
                                                data-testid="tonk-sort-rank"
                                                title="Sort your hand by rank"
                                            >
                                                Rank
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => sortHandBy('suit')}
                                                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border border-white/15 text-white/70 hover:text-[#F5C542] hover:border-[#F5C542]/60 transition"
                                                data-testid="tonk-sort-suit"
                                                title="Sort your hand by suit"
                                            >
                                                Suit
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="text-[10px] text-white/50 mb-1">
                                {swapIdx != null
                                    ? 'tap another card to swap positions'
                                    : selected.length > 0
                                        ? `${selected.length} selected`
                                        : (isMyTurn && hasDrawn
                                            ? 'swipe \u2191 to discard'
                                            : (isMyTurn && !hasDrawn
                                                ? '\u2b06\ufe0f  Draw a card first'
                                                : (canReorder
                                                    ? (pinnedKeys.size > 0
                                                        ? `${pinnedKeys.size} pinned · tap to swap · hold to pin/unpin`
                                                        : 'tap to swap · hold to pin · sort by rank/suit')
                                                    : (mySeat && table?.turn_index != null && table?.seats?.[table.turn_index]
                                                        ? `Waiting for ${table.seats[table.turn_index].display_name || 'next player'}\u2026`
                                                        : 'tap to select'))))}
                            </div>
                            <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar justify-center">
                                {orderedHand.map((c, idx) => {
                                    const isSwapPicked = swapIdx === idx;
                                    const isPinned = pinnedKeys.has(cardKey(c));
                                    // On reorder-mode: tap starts/completes a swap; long-press pins/unpins.
                                    // On my turn (post-draw): quick-tap discards, long-press selects for spread.
                                    const onTap = canReorder
                                        ? () => beginOrSwap(idx)
                                        : () => toggleSelectCard(c);
                                    // Discard on tap when it's a legal move.
                                    // Otherwise fire a helpful toast so the
                                    // player knows *why* the tap seemed to
                                    // do nothing — reported by user (Iter
                                    // 278): "I keep tapping cards and
                                    // nothing happens" — turned out she
                                    // hadn't drawn yet or the turn had
                                    // already passed.
                                    const explainNoop = () => {
                                        if (!isMyTurn) {
                                            const turnName = table?.seats?.[table.turn_index]?.display_name || 'the next player';
                                            toast.info(`It's ${turnName}'s turn — hang tight.`);
                                            return;
                                        }
                                        if (!hasDrawn) {
                                            toast.info('Draw a card first (tap the deck or the discard pile).');
                                            return;
                                        }
                                        if (selected.length > 0) {
                                            toast.info('Long-press to un-select those cards first, then tap to discard.');
                                            return;
                                        }
                                    };
                                    const onQuickTap = canReorder
                                        ? () => beginOrSwap(idx)
                                        : (isMyTurn && hasDrawn && selected.length === 0
                                            ? (card) => sendAction({ action: 'discard', card })
                                            : () => { explainNoop(); toggleSelectCard(c); });
                                    const onLongPress = canReorder
                                        ? togglePin
                                        : null;
                                    return (
                                        <div
                                            key={cardKey(c)}
                                            className={isSwapPicked ? 'transform -translate-y-2 transition ring-2 ring-[#F5C542] rounded-md' : 'transition'}
                                            data-testid={isSwapPicked ? `tonk-hand-card-picked-${idx}` : undefined}
                                        >
                                            <CardFace
                                                card={c}
                                                selected={selected.some((s) => cardKey(s) === cardKey(c))}
                                                suggested={spreadSuggestions.has(cardKey(c))}
                                                pinned={isPinned}
                                                onClick={onTap}
                                                onQuickTap={onQuickTap}
                                                onLongPress={onLongPress}
                                                onSwipeUp={
                                                    isMyTurn && hasDrawn
                                                        ? (card) => sendAction({ action: 'discard', card })
                                                        : null
                                                }
                                                disabled={!isMyTurn && !canReorder}
                                                dim={!isMyTurn && !canReorder}
                                            />
                                        </div>
                                    );
                                })}
                                {(!mySeat.hand || mySeat.hand.length === 0) && (
                                    mySeat.is_out && table.phase === 'in_hand'
                                        ? (
                                            <div
                                                className="flex flex-col items-center justify-center py-3 px-4 rounded-lg border border-[#F5C542]/40 bg-[#F5C542]/5"
                                                data-testid="tonk-wait-next-hand"
                                            >
                                                <span className="text-[#F5C542] font-bold text-sm">You&rsquo;re in — hang tight</span>
                                                <span className="text-white/60 text-[11px] mt-0.5">
                                                    Cards will deal at the start of the next hand
                                                </span>
                                            </div>
                                        )
                                        : <span className="text-white/40 text-xs italic py-3">No cards yet — waiting for the deal.</span>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                                {canStart && (
                                    <Button
                                        onClick={startHand}
                                        disabled={starting}
                                        className="col-span-4 font-black text-black h-9 tracking-wider"
                                        style={{ background: THEME.accent }}
                                        data-testid="tonk-start-btn"
                                    >
                                        <PlayCircle className="w-4 h-4 mr-1" /> {starting ? 'Dealing…' : 'DEAL HAND'}
                                    </Button>
                                )}

                                {table.phase === 'in_hand' && (
                                    <>
                                        {/* Auto-detected spread — shows only when I actually have one */}
                                        {bestSpread && isMyTurn && hasDrawn && (
                                            <Button
                                                onClick={layBestSpread}
                                                className="col-span-4 h-9 font-black text-[13px] tracking-wider text-black"
                                                style={{
                                                    background: 'linear-gradient(90deg,#F5C542,#E7A100)',
                                                    boxShadow: '0 0 14px rgba(245,197,66,0.5)',
                                                }}
                                                data-testid="tonk-drop-spread-btn"
                                                title="Lay this spread on the felt"
                                            >
                                                <Layers className="w-4 h-4 mr-1" /> DROP SPREAD ({bestSpread.label})
                                            </Button>
                                        )}

                                        {/* Available hits on any laid spread */}
                                        {availableHits.length > 0 && isMyTurn && (
                                            <div className="col-span-4 flex flex-wrap gap-1.5" data-testid="tonk-hit-rail">
                                                <span className="text-[10px] text-white/50 font-mono self-center mr-1">HITS:</span>
                                                {availableHits.slice(0, 4).map((h, i) => (
                                                    <Button
                                                        key={`hit-${h.target_seat}-${h.spread_index}-${h.card[0]}-${h.card[1]}-${i}`}
                                                        onClick={() => hitOne(h)}
                                                        className="h-8 px-2 font-black text-[11px] tracking-wider border"
                                                        style={{
                                                            background: 'rgba(34,197,94,0.15)',
                                                            borderColor: 'rgba(34,197,94,0.6)',
                                                            color: '#4ADE80',
                                                        }}
                                                        data-testid={`tonk-hit-btn-${i}`}
                                                        title="Extend an opponent's spread"
                                                    >
                                                        HIT {h.label}
                                                    </Button>
                                                ))}
                                                {availableHits.length > 4 && (
                                                    <span className="text-[10px] text-white/40 self-center">
                                                        +{availableHits.length - 4} more…
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        <Button
                                            onClick={discardSelected}
                                            disabled={!isMyTurn || !hasDrawn || selected.length !== 1}
                                            className="h-9 font-black text-[13px] tracking-wider bg-white/10 hover:bg-white/20 border border-white/20"
                                            data-testid="tonk-discard-btn"
                                        >
                                            DISCARD
                                        </Button>
                                        <Button
                                            onClick={spreadSelected}
                                            disabled={!isMyTurn || !hasDrawn || selected.length < 3}
                                            className="h-9 font-black text-[13px] tracking-wider bg-white/10 hover:bg-white/20 border border-white/20"
                                            data-testid="tonk-spread-btn"
                                            title="Lay the cards you've selected as a spread"
                                        >
                                            <Layers className="w-4 h-4 mr-1" /> LAY PICKS
                                        </Button>
                                        <Button
                                            onClick={drop}
                                            disabled={!isMyTurn}
                                            className="h-9 font-black text-[13px] tracking-wider bg-orange-600/70 hover:bg-orange-600 border border-orange-400/30"
                                            data-testid="tonk-drop-btn"
                                            title="Go down — bet you have the lowest hand"
                                        >
                                            <DoorOpen className="w-4 h-4 mr-1" /> GO DOWN
                                        </Button>
                                        <Button
                                            onClick={tonkOut}
                                            disabled={!isMyTurn || !hasDrawn || (mySeat.hand?.length ?? 1) > 0}
                                            className="h-9 font-black text-[13px] tracking-wider text-black"
                                            style={{ background: (mySeat.hand?.length ?? 1) === 0 ? THEME.accent : 'rgba(245,197,66,0.15)' }}
                                            data-testid="tonk-tonkout-btn"
                                            title="Empty hand — go out"
                                        >
                                            <ChevronsUp className="w-4 h-4 mr-1" /> TONK!
                                        </Button>
                                    </>
                                )}
                                {table.phase === 'hand_ended' && canStart && null /* start button shown above */}
                                {table.phase === 'hand_ended' && !canStart && (
                                    <div
                                        className="col-span-4 flex items-center justify-center gap-3 py-2"
                                        data-testid="tonk-next-hand-countdown"
                                    >
                                        <span className="text-[#F5C542] text-xs font-bold">
                                            {nextHandSecs != null && nextHandSecs > 0
                                                ? `Next hand in ${nextHandSecs}s…`
                                                : 'Dealing next hand…'}
                                        </span>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    await axios.post(
                                                        `${API}/tonk/tables/${tableId}/deal-now`,
                                                        {},
                                                        { withCredentials: true },
                                                    );
                                                } catch (e) {
                                                    const msg = e?.response?.data?.detail || 'Could not deal now';
                                                    toast.error(msg);
                                                }
                                            }}
                                            className="px-3 py-1 rounded-full text-[11px] font-black text-black transition"
                                            style={{ background: THEME.accent }}
                                            data-testid="tonk-deal-now-btn"
                                            title="Skip the countdown — deal the next hand right now"
                                        >
                                            Deal now →
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-3 space-y-2" data-testid="tonk-spectator-panel">
                            {needsFunds ? (
                                <>
                                    <p className="text-sm text-white/90">
                                        This is a <span className="font-bold text-[#F5C542]">cash table</span> —
                                        ante {fmt$(needsFunds.needed)} per hand.
                                    </p>
                                    <p className="text-[11px] text-white/50">
                                        Your wallet is a bit short. Load funds to grab a seat.
                                    </p>
                                    <div className="flex gap-2 justify-center pt-1">
                                        <Button
                                            onClick={() => nav(`/profile?return=${encodeURIComponent(`/casino/tonk/${tableId}`)}#deposit`)}
                                            className="h-9 font-black text-[13px] tracking-wider text-black"
                                            style={{ background: THEME.accent }}
                                            data-testid="tonk-load-funds-btn"
                                        >
                                            <Layers className="w-4 h-4 mr-1" /> LOAD FUNDS
                                        </Button>
                                        <Button
                                            onClick={() => setNeedsFunds(null)}
                                            className="h-9 font-black text-[13px] tracking-wider bg-white/10 hover:bg-white/20 border border-white/20"
                                            data-testid="tonk-load-funds-later"
                                        >
                                            SPECTATE
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-white/90">
                                        {(table.seats || []).some((s) => !s.user_id && !s.is_bot)
                                            ? "There's an open seat — jump in!"
                                            : "Table is full — you're spectating."}
                                    </p>
                                    {(table.seats || []).some((s) => !s.user_id && !s.is_bot) && (
                                        <Button
                                            onClick={() => takeSeat()}
                                            disabled={joining}
                                            className="h-10 px-6 font-black text-[13px] tracking-wider text-black"
                                            style={{ background: THEME.accent }}
                                            data-testid="tonk-take-seat-btn"
                                        >
                                            {joining ? 'Taking a seat…' : 'TAKE A SEAT'}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Avatar picker modal */}
            <AvatarPicker
                open={avatarOpen}
                onClose={() => setAvatarOpen(false)}
                currentKind={myAvatar.kind}
                currentValue={myAvatar.value}
                onSaved={({ kind, value }) => {
                    setMyAvatar({ kind, value });
                    toast.success('Look updated');
                }}
            />

            {/* Flying-cards dealing animation (mounts on new hand) */}
            <TonkDealAnim
                key={dealAnimId}
                active={dealActive}
                dealerSeatIndex={table.dealer_index}
                recipientSeatIndices={dealRecipientOrder}
                mySeatIndex={mySeat?.seat_index ?? -1}
                onComplete={() => setDealActive(false)}
            />

            {/* ─── Hand-end display ─────────────────────────────────────
                BIG DEAL overlay for dramatic wins (tonk-out, catch, doubles);
                a quieter top banner for regular clean drops. */}
            <HandEndBoundary>
                <HandEndOverlay
                    banner={handEndBanner}
                    table={table}
                    userId={user?.id}
                    celebrationKey={celebrationKey}
                />
            </HandEndBoundary>
        </div>
    );
}

// ─── Hand-end error boundary ────────────────────────────────────────
// Reported 2026-07-23: "A hand was won and the screen just went blank."
// Video analysis showed a full React tree crash right after the winner
// banner rendered — for the winning player only. If ANY child render
// throws (bad data in `banner.payout`, missing seat, canvas gremlin in
// WinShareButton, etc.) we must NOT unmount the entire /tonk page and
// leave the player staring at a black void. This boundary swallows the
// render error, toasts once, and keeps the felt visible.
class HandEndBoundary extends React.Component {
    constructor(props) { super(props); this.state = { failed: false }; }
    static getDerivedStateFromError() { return { failed: true }; }
    componentDidCatch(err, info) {
        // Best-effort visibility — one toast so the user knows the
        // celebration overlay was skipped, plus a console entry so
        // we can capture the stack on production repros.
        try {
            console.error('HandEndOverlay crashed:', err, info);
            toast.error('Winner overlay glitch — next hand is still on the way.');
        } catch { /* ignore */ }
    }
    render() {
        if (this.state.failed) return null;
        return this.props.children;
    }
}

// ─── Hand-end celebration ────────────────────────────────────────────
// Distinguishes a "big deal" hand (tonk-out, catch, or any multiplier > 1)
// from a routine clean drop. Big deals get a full-screen dramatic overlay
// with a confetti burst; routine drops get a modest top banner so the
// screen doesn't feel obnoxious for every hand.
function HandEndOverlay({ banner, table, userId, celebrationKey }) {
    if (!banner || !table) return null;
    const kind = banner.kind || '';
    const mult = Number(banner.multiplier || 1);
    const isBigDeal = kind === 'tonk_out' || kind === 'auto_tonk' || kind === 'catch' || mult > 1;
    const winnerSeat = table.seats[banner.winner_seat];
    const winnerName = winnerSeat?.user_id && winnerSeat.user_id === userId
        ? 'YOU'
        : (winnerSeat?.display_name || 'Winner');
    const label = (
        kind === 'tonk_out' ? 'TONK OUT!' :
        kind === 'auto_tonk' ? 'AUTO-TONK!' :
        kind === 'catch' ? 'CAUGHT!' :
        mult > 1 ? `DOUBLE UP · ${mult}×` :
        'HAND WON'
    );
    const subtitle = (
        kind === 'tonk_out' ? 'Cleared the hand' :
        kind === 'auto_tonk' ? 'Dealt with an instant win' :
        kind === 'catch' ? 'Called the drop — and caught it' :
        mult > 1 ? 'Big multiplier bonus' :
        'Clean drop'
    );

    if (!isBigDeal) {
        // Regular drop — small top banner, unchanged look.
        return (
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="fixed top-16 left-1/2 -translate-x-1/2 z-40 rounded-2xl border-2 px-5 py-3 max-w-md w-[90%]"
                    style={{
                        background: 'rgba(3,11,8,0.95)',
                        borderColor: THEME.accent,
                        boxShadow: `0 0 32px ${THEME.accent}66`,
                    }}
                    data-testid="tonk-hand-end-banner"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <Trophy className="w-5 h-5" style={{ color: THEME.accent }} />
                        <span
                            className="font-black text-lg"
                            style={{ fontFamily: THEME.headingFont, letterSpacing: THEME.headingLetter, color: THEME.accent }}
                        >
                            {label}
                        </span>
                    </div>
                    <p className="text-sm text-white/90">
                        {winnerName} wins <span className="font-bold text-[#F5C542]">{fmt$(banner.payout)}</span>
                    </p>
                </motion.div>
            </AnimatePresence>
        );
    }

    // BIG DEAL overlay — full-screen centered card + confetti + shake.
    return (
        <AnimatePresence>
            <motion.div
                key={celebrationKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none"
                data-testid="tonk-hand-end-banner"
            >
                {/* Backdrop dim */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.85) 70%)',
                    }}
                />
                {/* Confetti burst */}
                <ConfettiBurst count={26} />
                {/* Winner card */}
                <motion.div
                    initial={{ scale: 0.5, opacity: 0, y: 20 }}
                    animate={{
                        scale: [0.5, 1.08, 1],
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        duration: 0.7,
                        times: [0, 0.55, 1],
                        ease: [0.22, 1, 0.36, 1],
                    }}
                    className="relative z-10 rounded-3xl border-2 px-8 py-6 max-w-sm w-[86%] text-center"
                    style={{
                        background: 'linear-gradient(180deg, rgba(24,60,38,0.98) 0%, rgba(3,11,8,0.98) 100%)',
                        borderColor: THEME.accent,
                        boxShadow: `0 0 80px ${THEME.accent}aa, inset 0 0 24px rgba(245,197,66,0.15)`,
                    }}
                    data-testid="tonk-bigdeal-card"
                >
                    <motion.div
                        animate={{ rotate: [-6, 6, -4, 4, 0], scale: [1, 1.15, 1] }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="inline-flex mb-2"
                    >
                        <Trophy className="w-14 h-14" style={{ color: THEME.accent, filter: `drop-shadow(0 0 16px ${THEME.accent})` }} />
                    </motion.div>
                    <div
                        className="font-black text-3xl leading-tight"
                        style={{
                            fontFamily: THEME.headingFont,
                            letterSpacing: '0.06em',
                            color: THEME.accent,
                            textShadow: `0 0 22px ${THEME.accent}66`,
                        }}
                        data-testid="tonk-bigdeal-label"
                    >
                        {label}
                    </div>
                    <div className="text-white/70 text-xs uppercase tracking-widest mt-1">
                        {subtitle}
                    </div>
                    <div className="mt-4 mb-1 text-white font-bold text-xl">
                        {winnerName}
                    </div>
                    <motion.div
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.35, duration: 0.4, type: 'spring', stiffness: 260 }}
                        className="text-5xl font-black font-mono"
                        style={{
                            color: THEME.accent,
                            textShadow: `0 0 24px ${THEME.accent}, 0 0 40px ${THEME.accent}66`,
                        }}
                        data-testid="tonk-bigdeal-payout"
                    >
                        {fmt$(banner.payout)}
                    </motion.div>
                    {mult > 1 && (
                        <div
                            className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-black"
                            style={{
                                background: THEME.accent,
                                color: '#0b1a10',
                                letterSpacing: '0.08em',
                            }}
                            data-testid="tonk-bigdeal-multiplier"
                        >
                            {mult}× MULTIPLIER
                        </div>
                    )}
                    {/* Share — only when the current player is the winner.
                        Delayed reveal so it doesn't compete with the payout
                        animation. */}
                    {winnerName === 'YOU' && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9, duration: 0.35 }}
                            className="mt-4 flex justify-center pointer-events-auto"
                        >
                            <WinShareButton
                                label={label}
                                subtitle={subtitle}
                                payout={banner.payout}
                                multiplier={mult}
                                winnerName={winnerSeat?.display_name || 'You'}
                            />
                        </motion.div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ─── Confetti burst — CSS-only radial particle explosion ──────────
// Renders `count` gold/red/white particles that fly outward from the
// center over ~1.2 seconds. Zero JS animation loop; pure CSS keyframes.
function ConfettiBurst({ count = 24 }) {
    const particles = useMemo(() => {
        const colors = ['#F5C542', '#F5C542', '#F5C542', '#ffffff', '#ff5c8a', '#ffb340'];
        const arr = [];
        for (let i = 0; i < count; i++) {
            const angle = (Math.PI * 2 * i) / count + Math.random() * 0.35;
            const distance = 130 + Math.random() * 140; // px
            const dx = Math.cos(angle) * distance;
            const dy = Math.sin(angle) * distance;
            arr.push({
                id: i,
                dx: `${dx}px`,
                dy: `${dy}px`,
                rot: `${Math.round((Math.random() - 0.5) * 720)}deg`,
                color: colors[i % colors.length],
                delay: Math.random() * 0.1,
                shape: i % 3, // 0 dot, 1 square, 2 pill
            });
        }
        return arr;
    }, [count]);

    return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0"
             aria-hidden="true"
             data-testid="tonk-confetti">
            <style>{`
                @keyframes tonkConfettiFly {
                    0%   { transform: translate(0,0) rotate(0deg) scale(0.4); opacity: 0; }
                    12%  { opacity: 1; }
                    100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(1); opacity: 0; }
                }
            `}</style>
            {particles.map((p) => (
                <span
                    key={p.id}
                    style={{
                        position: 'absolute',
                        width: p.shape === 2 ? '10px' : '8px',
                        height: p.shape === 2 ? '3px' : '8px',
                        borderRadius: p.shape === 1 ? '2px' : '999px',
                        background: p.color,
                        boxShadow: `0 0 8px ${p.color}aa`,
                        animation: `tonkConfettiFly 1200ms cubic-bezier(0.15, 0.9, 0.3, 1) ${p.delay}s forwards`,
                        '--dx': p.dx,
                        '--dy': p.dy,
                        '--rot': p.rot,
                    }}
                />
            ))}
        </div>
    );
}

// ─── Win-share button ────────────────────────────────────────────────
// Draws a branded 1080×1080 image of the win on a canvas, then uses the
// Web Share API to hand it to whichever social/messenger the user picks.
// Falls back to a PNG download if `navigator.share` isn't available.
function WinShareButton({ label, subtitle, payout, multiplier, winnerName }) {
    const [busy, setBusy] = useState(false);

    const buildImage = useCallback(async () => {
        const size = 1080;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        // Background — dark green gradient with subtle radial glow.
        const bg = ctx.createLinearGradient(0, 0, 0, size);
        bg.addColorStop(0, '#0d2a1a');
        bg.addColorStop(1, '#020806');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, size, size);

        const glow = ctx.createRadialGradient(size / 2, size * 0.42, 40, size / 2, size * 0.42, size * 0.55);
        glow.addColorStop(0, 'rgba(245,197,66,0.30)');
        glow.addColorStop(1, 'rgba(245,197,66,0)');
        ctx.fillStyle = glow;
        ctx.fillRect(0, 0, size, size);

        // Gold border frame
        ctx.strokeStyle = THEME.accent;
        ctx.lineWidth = 6;
        const pad = 40;
        ctx.strokeRect(pad, pad, size - pad * 2, size - pad * 2);

        // BETohalics wordmark — top
        ctx.fillStyle = THEME.accent;
        ctx.font = 'bold 42px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('BETohalics', size / 2, 130);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.font = '22px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.fillText('GOOD OL\u2019 TONK', size / 2, 165);

        // Big label ("TONK OUT!", "CAUGHT!", etc.)
        ctx.fillStyle = THEME.accent;
        ctx.font = 'bold 96px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.fillText(label, size / 2, 340);

        // Subtitle
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.font = '26px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.fillText(subtitle.toUpperCase(), size / 2, 385);

        // Trophy (draw a simple gold cup — no icon library on canvas)
        drawTrophy(ctx, size / 2, 490, 80, THEME.accent);

        // Winner name
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 44px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.fillText(winnerName || 'YOU', size / 2, 640);

        // Massive payout — gold with soft glow
        ctx.save();
        ctx.shadowColor = THEME.accent;
        ctx.shadowBlur = 40;
        ctx.fillStyle = THEME.accent;
        ctx.font = 'bold 180px "SF Mono", "JetBrains Mono", monospace';
        ctx.fillText(fmt$(payout), size / 2, 830);
        ctx.restore();

        // Multiplier pill
        if (multiplier > 1) {
            const pillText = `${multiplier}× MULTIPLIER`;
            ctx.font = 'bold 32px system-ui, -apple-system, "Segoe UI", sans-serif';
            const w = ctx.measureText(pillText).width + 56;
            const x = (size - w) / 2;
            const y = 870;
            const h = 60;
            ctx.fillStyle = THEME.accent;
            roundRect(ctx, x, y, w, h, 30);
            ctx.fill();
            ctx.fillStyle = '#0b1a10';
            ctx.fillText(pillText, size / 2, y + 42);
        }

        // Footer tagline
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.font = '26px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.fillText('Join the game at', size / 2, 990);
        ctx.fillStyle = THEME.accent;
        ctx.font = 'bold 32px system-ui, -apple-system, "Segoe UI", sans-serif';
        const host = (window.location.host || 'betohalics').replace('www.', '');
        ctx.fillText(host, size / 2, 1030);

        return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.92));
    }, [label, subtitle, payout, multiplier, winnerName]);

    const onShare = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            const blob = await buildImage();
            if (!blob) {
                toast.error('Could not build the share image');
                return;
            }
            const file = new File([blob], `betohalics-win-${Date.now()}.png`, { type: 'image/png' });
            const text = `${label} — I just won ${fmt$(payout)} playing Good Ol' Tonk on BETohalics!`;
            const url = window.location.origin + '/casino/tonk';
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], text, url, title: label });
            } else if (navigator.share) {
                await navigator.share({ text, url, title: label });
            } else {
                // Fallback — download the PNG so the user can save + share manually.
                const dl = document.createElement('a');
                dl.href = URL.createObjectURL(blob);
                dl.download = file.name;
                document.body.appendChild(dl);
                dl.click();
                document.body.removeChild(dl);
                setTimeout(() => URL.revokeObjectURL(dl.href), 5000);
                toast.success('Win image downloaded');
            }
        } catch (err) {
            // User cancel: navigator.share throws AbortError. Silent.
            if (err?.name !== 'AbortError') {
                toast.error('Sharing failed');
            }
        } finally {
            setBusy(false);
        }
    }, [busy, buildImage, label, payout]);

    return (
        <button
            type="button"
            onClick={onShare}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full border-2 px-5 py-2 text-sm font-bold transition disabled:opacity-60"
            style={{
                background: 'rgba(245,197,66,0.12)',
                borderColor: THEME.accent,
                color: THEME.accent,
                boxShadow: `0 0 20px ${THEME.accent}55`,
            }}
            data-testid="tonk-bigdeal-share"
            aria-label="Share this win"
        >
            <Share2 className="w-4 h-4" />
            {busy ? 'Preparing…' : 'Share this win'}
        </button>
    );
}

// Helper — rounded rect path for canvas.
function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

// Helper — draw a simple gold trophy silhouette centered at (cx, cy).
function drawTrophy(ctx, cx, cy, size, color) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 24;
    // Cup body
    ctx.beginPath();
    ctx.moveTo(cx - size * 0.5, cy - size * 0.5);
    ctx.lineTo(cx + size * 0.5, cy - size * 0.5);
    ctx.lineTo(cx + size * 0.35, cy + size * 0.15);
    ctx.lineTo(cx - size * 0.35, cy + size * 0.15);
    ctx.closePath();
    ctx.fill();
    // Base stem
    ctx.fillRect(cx - size * 0.09, cy + size * 0.15, size * 0.18, size * 0.22);
    // Base plate
    ctx.fillRect(cx - size * 0.34, cy + size * 0.37, size * 0.68, size * 0.12);
    // Ear handles
    ctx.lineWidth = size * 0.09;
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.arc(cx - size * 0.5, cy - size * 0.15, size * 0.22, Math.PI * 0.5, Math.PI * 1.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx + size * 0.5, cy - size * 0.15, size * 0.22, Math.PI * 1.5, Math.PI * 0.5);
    ctx.stroke();
    ctx.restore();
}

